<template>
  <div class="c1-main c1-finance-page" @click="c1HamMenuOpen = false; c1ShiftMenuOpen = false; c1TopTab = null">

    <!-- PASSWORD GATE OVERLAY -->
    <div v-if="!f1Unlocked" class="f1-pw-overlay">
      <div class="f1-pw-card">
        <div class="f1-pw-icon">
          <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>
        <div class="f1-pw-title">Finance Area — Enter Password</div>
        <div class="f1-pw-sub">This module is password-protected. Enter your credentials to continue.</div>
        <input
          class="f1-pw-input"
          type="password"
          v-model="f1GatePassword"
          placeholder="Enter your password…"
          @keyup.enter="f1VerifyPassword"
          autofocus
        />
        <div v-if="f1GateError" class="f1-pw-error">{{ f1GateError }}</div>
        <button class="f1-pw-submit" @click="f1VerifyPassword" :disabled="f1GateLoading || !f1GatePassword">
          {{ f1GateLoading ? 'Verifying…' : 'Unlock Finance' }}
        </button>
      </div>
    </div>

    <!-- TOAST -->
    <transition name="fi1-toast-fade">
      <div v-if="f1Toast" class="fi1-toast" :style="{ background: f1Toast.color }">{{ f1Toast.msg }}</div>
    </transition>

    <!-- TOP BAR -->
    <div class="c1-sticky-top">
    <div class="c1-tib" @click="c1SlideOpen = null; c1HamMenuOpen = false; c1ShiftMenuOpen = false">
      <!-- LEFT 25% — Viki face flush left -->
      <div class="c1-tib-side c1-tib-left" style="padding-left:0;gap:8px;">
        <div class="c1-viki-topface"
          :class="{ 'c1-viki-topface--active': c1PanelActiveContent === 'viki' }"
          @click.stop="c1SelectPanel('viki','')"
        >
          <viki-face :mood="c1VikiMood" :speaking="c1VikiSpeaking" :size="44" style="width:72px;height:44px;display:block;overflow:hidden;border-radius:0;"/>
        </div>
        <div class="c1-shift-wrap" @click.stop="c1ShiftMenuOpen = !c1ShiftMenuOpen; c1HamMenuOpen = false">
          <div class="c1-tib-shift-pill" :class="{ open: c1ShiftMenuOpen }">
            ⏱ {{ c1ShiftElapsed }}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width:10px;height:10px;margin-left:3px;transition:transform .2s" :style="{ transform: c1ShiftMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)' }"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
        </div>
      </div>
      <!-- CENTER 50% -->
      <div class="c1-tib-center">
        <div class="c1-tib-mod-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:14px;height:14px;flex-shrink:0"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
          Finance
        </div>
      </div>
      <!-- RIGHT 25% -->
      <div class="c1-tib-side c1-tib-right">
        <button class="c1-tib-icon-btn f1-export-btn" :class="{ loading: f1ExportLoading }" @click.stop="f1ExportCSV()" title="Export finance data as CSV">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:15px;height:15px"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
        </button>
        <div class="f1-add-btn" @click.stop="f1OpenAddSlide">
          <svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          <span>Add</span>
        </div>
        <div class="c1-ham-wrap" @click.stop>
          <button class="c1-ham-btn" :class="{ open: c1HamMenuOpen }" @click="c1HamMenuOpen = !c1HamMenuOpen; c1ShiftMenuOpen = false">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width:18px;height:18px"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          </button>
        </div>
      </div>
    </div>
    <!-- SHIFT DRAWER -->
    <div class="c1-top-drawer c1-shift-drawer" :class="{ open: c1ShiftMenuOpen }" @click.stop>
      <div class="c1-td-inner">
        <div class="c1-td-section-lbl">My Shift</div>
        <div class="c1-td-status-row">
          <div class="c1-td-stat">
            <div class="c1-td-stat-val c1-mono">{{ c1ShiftStartTime ? new Date(c1ShiftStartTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—' }}</div>
            <div class="c1-td-stat-lbl">Started</div>
          </div>
          <div class="c1-td-stat">
            <div class="c1-td-stat-val c1-mono" style="color:var(--c1-dp)">{{ c1ShiftElapsed }}</div>
            <div class="c1-td-stat-lbl">Elapsed</div>
          </div>
          <div class="c1-td-stat">
            <div class="c1-td-stat-val">{{ c1BathroomCount }}×</div>
            <div class="c1-td-stat-lbl">🚽 Bathroom</div>
          </div>
          <div class="c1-td-stat">
            <div class="c1-td-stat-val">{{ c1CoffeeCount }}×</div>
            <div class="c1-td-stat-lbl">☕ Coffee</div>
          </div>
        </div>
        <div class="c1-td-btn-row">
          <button class="c1-td-btn c1-td-btn-shift"   :class="{ active: c1Status === 'shift' }"   @click.stop="c1SetStatus('shift')">▶ On Shift</button>
          <button class="c1-td-btn c1-td-btn-bathroom" :class="{ active: c1Status === 'bathroom' }" @click.stop="c1SetStatus('bathroom')">🚽 Bathroom</button>
          <button class="c1-td-btn c1-td-btn-coffee"  :class="{ active: c1Status === 'coffee' }"  @click.stop="c1SetStatus('coffee')">☕ Coffee</button>
          <button class="c1-td-btn c1-td-btn-break"   :class="{ active: c1Status === 'break' }"   @click.stop="c1SetStatus('break')">⏸ Break</button>
          <button class="c1-td-btn c1-td-btn-lunch"   :class="{ active: c1Status === 'lunch' }"   @click.stop="c1SetStatus('lunch')">🍽 Lunch</button>
          <button class="c1-td-btn c1-td-btn-end"     @click.stop="c1SetStatus('end')">⏹ End Shift</button>
        </div>
      </div>
    </div>
    <!-- HAM NAV DRAWER -->
    <div class="c1-top-drawer c1-ham-drawer" :class="{ open: c1HamMenuOpen }" @click.stop>
      <div class="c1-td-inner c1-td-nav-grid">
        <router-link to="/clients1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:var(--c1-dp)"></span>Clients</router-link>
        <router-link to="/tasks1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:var(--c1-sc)"></span>Tasks</router-link>
        <router-link to="/stats1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:var(--c1-ld)"></span>Statistics</router-link>
        <router-link to="/chat1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:var(--c1-rf)"></span>Chat</router-link>
        <router-link to="/communications1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#3b82f6"></span>Communications</router-link>
        <router-link to="/affiliation1" class="c1-td-nav-item" :class="{ on: $route.path === '/affiliation1' }" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#8b5cf6"></span>Affiliation</router-link>
        <router-link to="/finance1" class="c1-td-nav-item" :class="{ on: $route.path === '/finance1' }" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#10b981"></span>Finance</router-link>
        <router-link to="/shifts1" class="c1-td-nav-item" :class="{ on: $route.path === '/shifts1' }" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#f59e0b"></span>Shifts</router-link>
        <router-link to="/users1" class="c1-td-nav-item" :class="{ on: $route.path === '/users1' }" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#06b6d4"></span>Users</router-link>
        <router-link to="/brokers1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#ec4899"></span>Brokers</router-link>
        <router-link to="/security1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#ef4444"></span>Security</router-link>
        <router-link to="/quiz1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#a78bfa"></span>Quiz</router-link>
        <router-link to="/instruction1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#64748b"></span>Instructions</router-link>
        <router-link to="/settings1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#94a3b8"></span>Settings</router-link>
        <router-link to="/profile1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#22d3ee"></span>My Profile</router-link>
      </div>
    </div>
    </div><!-- /c1-sticky-top -->

    <!-- TOP STRIP (filter tabs - always visible) -->
    <div class="c1-top-strip c1-mod-strip" @click.stop>
      <div class="c1-top-strip-body">
        <div class="c1-ts-tab-row">
          <!-- Date tab -->
          <div class="c1-ts-tab" :class="{on: c1TopTab === 'date'}" @click.stop="c1TopTab = c1TopTab === 'date' ? null : 'date'">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:12px;height:12px"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <span>Date</span>
            <svg class="c1-ts-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" :style="{transform: c1TopTab==='date' ? 'rotate(180deg)' : ''}"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
          <div class="c1-ts-tab-sep"></div>
          <!-- Category tab -->
          <div class="c1-ts-tab" :class="{on: c1TopTab === 'category'}" @click.stop="c1TopTab = c1TopTab === 'category' ? null : 'category'">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:12px;height:12px"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
            <span>Category</span>
            <svg class="c1-ts-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" :style="{transform: c1TopTab==='category' ? 'rotate(180deg)' : ''}"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
          <div class="c1-ts-tab-sep"></div>
          <!-- Status tab -->
          <div class="c1-ts-tab" :class="{on: c1TopTab === 'status'}" @click.stop="c1TopTab = c1TopTab === 'status' ? null : 'status'">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:12px;height:12px"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <span>Status</span>
            <svg class="c1-ts-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" :style="{transform: c1TopTab==='status' ? 'rotate(180deg)' : ''}"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
          <div class="c1-ts-tab-sep"></div>
          <!-- Amount tab -->
          <div class="c1-ts-tab" :class="{on: c1TopTab === 'amount'}" @click.stop="c1TopTab = c1TopTab === 'amount' ? null : 'amount'">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:12px;height:12px"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
            <span>Amount</span>
            <svg class="c1-ts-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" :style="{transform: c1TopTab==='amount' ? 'rotate(180deg)' : ''}"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
          <div class="c1-ts-tab-sep"></div>
          <!-- Payment tab -->
          <div class="c1-ts-tab" :class="{on: c1TopTab === 'payment'}" @click.stop="c1TopTab = c1TopTab === 'payment' ? null : 'payment'">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:12px;height:12px"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
            <span>Payment</span>
            <svg class="c1-ts-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" :style="{transform: c1TopTab==='payment' ? 'rotate(180deg)' : ''}"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
          <div class="c1-ts-tab-sep"></div>
          <!-- Charts tab -->
          <div class="c1-ts-tab" :class="{on: c1TopTab === 'charts'}" @click.stop="c1TopTab = c1TopTab === 'charts' ? null : 'charts'">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:12px;height:12px"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
            <span>Charts</span>
            <svg class="c1-ts-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" :style="{transform: c1TopTab==='charts' ? 'rotate(180deg)' : ''}"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
        </div>

        <!-- Date dropdown -->
        <div v-show="c1TopTab === 'date'" class="c1-ts-dropdown" @click.stop>
          <div v-if="f1ActiveFilterCount" class="c1-reset-bar" @click="f1ResetFilters">
            <svg viewBox="0 0 24 24"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.95"/></svg>
            Reset all filters
            <span class="c1-reset-bar-count">{{ f1ActiveFilterCount }}</span>
          </div>
          <div class="c1-fp-group c1-fp-full">
            <div class="c1-fp-lbl">Date range</div>
            <div class="c1-fp-opts">
              <div class="c1-fp-opt" :class="{ on: f1DateFilter === 'today' }"     @click="f1ApplyDateFilter('today')">T</div>
              <div class="c1-fp-opt" :class="{ on: f1DateFilter === 'yesterday' }" @click="f1ApplyDateFilter('yesterday')">Y</div>
              <div class="c1-fp-opt" :class="{ on: f1DateFilter === 'week' }"      @click="f1ApplyDateFilter('week')">TW</div>
              <div class="c1-fp-opt" :class="{ on: f1DateFilter === 'lastweek' }"  @click="f1ApplyDateFilter('lastweek')">LW</div>
              <div class="c1-fp-opt" :class="{ on: f1DateFilter === 'month' }"     @click="f1ApplyDateFilter('month')">TM</div>
              <div class="c1-fp-opt" :class="{ on: f1DateFilter === 'lastmonth' }" @click="f1ApplyDateFilter('lastmonth')">LM</div>
              <div class="c1-fp-opt" :class="{ on: f1DateFilter === 'all' }"       @click="f1ApplyDateFilter('all')">ALL</div>
            </div>
            <div class="c1-date-range-row" style="margin-top:8px">
              <input type="date" class="c1-date-input" v-model="f1Filters.date_from" @change="f1DateFilter='custom'; f1Fetch()" />
              <span class="c1-date-sep">→</span>
              <input type="date" class="c1-date-input" v-model="f1Filters.date_to"   @change="f1DateFilter='custom'; f1Fetch()" />
            </div>
          </div>
        </div>

        <!-- Category dropdown -->
        <div v-show="c1TopTab === 'category'" class="c1-ts-dropdown" @click.stop>
          <div class="c1-fp-group c1-fp-full">
            <div class="c1-fp-lbl">Category</div>
            <div class="c1-fp-opts" style="flex-wrap:wrap">
              <div class="c1-fp-opt" :class="{ on: !f1Filters.category_ids.length }" @click="f1Filters.category_ids = []; f1Fetch()">All</div>
              <div
                v-for="cat in f1Categories"
                :key="cat.id"
                class="c1-fp-opt"
                :class="{ on: f1Filters.category_ids.includes(cat.id) }"
                :style="f1Filters.category_ids.includes(cat.id) ? { background: f1CatColor(cat.id), color: '#fff', borderColor: f1CatColor(cat.id) } : {}"
                @click="f1ToggleCategoryFilter(cat.id)"
              >{{ cat.name }}</div>
            </div>
          </div>
          <div class="c1-fp-group c1-fp-full">
            <div class="c1-fp-lbl">Added by</div>
            <div class="c1-fp-opts" style="flex-wrap:wrap">
              <div class="c1-fp-opt" :class="{ on: !f1Filters.user_id }" @click="f1Filters.user_id = null; f1Fetch()">All</div>
              <div
                v-for="u in f1UserList"
                :key="u.id"
                class="c1-fp-opt"
                :class="{ on: f1Filters.user_id === u.id }"
                @click="f1Filters.user_id = u.id; f1Fetch()"
              >{{ u.first_name }} {{ (u.last_name || '')[0] || '' }}.</div>
            </div>
          </div>
        </div>

        <!-- Status dropdown -->
        <div v-show="c1TopTab === 'status'" class="c1-ts-dropdown" @click.stop>
          <div class="c1-fp-group c1-fp-full">
            <div class="c1-fp-lbl">Status</div>
            <div class="c1-fp-opts">
              <div class="c1-fp-opt" :class="{ on: !f1Filters.status }" @click="f1Filters.status = null; f1Fetch()">All</div>
              <div class="c1-fp-opt f1-status-approved" :class="{ on: f1Filters.status === 'approved' }" @click="f1Filters.status = 'approved'; f1Fetch()">Approved</div>
              <div class="c1-fp-opt f1-status-pending"  :class="{ on: f1Filters.status === 'pending' }"  @click="f1Filters.status = 'pending'; f1Fetch()">Pending</div>
              <div class="c1-fp-opt f1-status-rejected" :class="{ on: f1Filters.status === 'rejected' }" @click="f1Filters.status = 'rejected'; f1Fetch()">Rejected</div>
            </div>
          </div>
          <div class="c1-fp-group c1-fp-full">
            <div class="c1-fp-lbl">Search</div>
            <div class="c1-fp-search">
              <svg class="c1-fp-sico" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input class="c1-fp-sinput" v-model.trim="f1Filters.search" placeholder="Description, amount…" type="text" @input="f1DebouncedFetch" />
              <div v-if="f1Filters.search" class="c1-fp-sclear" @click="f1Filters.search = ''; f1Fetch()">
                <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </div>
            </div>
          </div>
        </div>

        <!-- Amount dropdown -->
        <div v-show="c1TopTab === 'amount'" class="c1-ts-dropdown" @click.stop>
          <div class="c1-fp-group c1-fp-full">
            <div class="c1-fp-lbl">Amount range</div>
            <div class="f1-range-row">
              <input type="number" class="c1-pkf-input f1-range-input" v-model.number="f1Filters.amount_min" placeholder="Min $" @change="f1Fetch()" />
              <span class="c1-date-sep">—</span>
              <input type="number" class="c1-pkf-input f1-range-input" v-model.number="f1Filters.amount_max" placeholder="Max $" @change="f1Fetch()" />
            </div>
          </div>
        </div>

        <!-- Payment dropdown -->
        <div v-show="c1TopTab === 'payment'" class="c1-ts-dropdown" @click.stop>
          <div class="c1-fp-group c1-fp-full">
            <div class="c1-fp-lbl">Payment method</div>
            <div class="c1-fp-opts">
              <div class="c1-fp-opt" :class="{ on: !f1Filters.payment_method }" @click="f1Filters.payment_method = null; f1Fetch()">All</div>
              <div v-for="m in f1PaymentMethods" :key="m" class="c1-fp-opt" :class="{ on: f1Filters.payment_method === m }" @click="f1Filters.payment_method = m; f1Fetch()">{{ m }}</div>
            </div>
          </div>
        </div>

        <!-- Charts dropdown -->
        <div v-show="c1TopTab === 'charts'" class="c1-ts-dropdown" @click.stop>

          <!-- By Team bar chart -->
          <div class="c1-co-lbl">Team 1 vs Team 2 — This Period</div>
          <div v-if="!f1HasUserTeamData()" class="f1-team-chart-nodata">
            Team-level filtering requires finance records to be user-linked. Contact admin.
          </div>
          <div v-else class="f1-team-bar-chart">
            <div class="f1-tbc-col">
              <div class="f1-tbc-track">
                <div class="f1-tbc-fill f1-tbc-t1" :style="{ height: f1TeamBarPct(1) + '%' }"></div>
              </div>
              <div class="f1-tbc-lbl">⚡ Team 1</div>
              <div class="f1-tbc-val">{{ f1TeamSpend(1) !== null ? f1FormatAmt(f1TeamSpend(1)) : '—' }}</div>
            </div>
            <div class="f1-tbc-col">
              <div class="f1-tbc-track">
                <div class="f1-tbc-fill f1-tbc-t2" :style="{ height: f1TeamBarPct(2) + '%' }"></div>
              </div>
              <div class="f1-tbc-lbl">🔥 Team 2</div>
              <div class="f1-tbc-val">{{ f1TeamSpend(2) !== null ? f1FormatAmt(f1TeamSpend(2)) : '—' }}</div>
            </div>
          </div>

          <div class="c1-co-lbl">Spend by category</div>
          <div class="f1-donut-wrap">
            <svg class="f1-donut-svg" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="14" fill="none" stroke="#e4e3df" stroke-width="4"/>
              <circle
                v-for="(seg, si) in f1DonutSegments"
                :key="si"
                cx="18" cy="18" r="14" fill="none"
                :stroke="seg.color" stroke-width="4"
                :stroke-dasharray="seg.dash + ' ' + (88 - seg.dash)"
                :stroke-dashoffset="'-' + seg.offset"
                stroke-linecap="round"
              />
            </svg>
            <div class="f1-donut-center">
              <div class="f1-donut-total">{{ f1FormatAmt(f1SummaryTotal) }}</div>
              <div class="f1-donut-lbl">total</div>
            </div>
          </div>
          <div class="f1-donut-legend">
            <div v-for="(seg, si) in f1DonutSegments" :key="'leg-'+si" class="f1-leg-row">
              <div class="f1-leg-dot" :style="{ background: seg.color }"></div>
              <span class="f1-leg-name">{{ seg.name }}</span>
              <span class="f1-leg-val">{{ f1FormatAmt(seg.amount) }}</span>
            </div>
          </div>
          <div class="c1-co-lbl" style="margin-top:18px">Budget vs Actual</div>
          <div class="f1-budget-rows">
            <div v-for="b in f1BudgetRows" :key="b.name" class="f1-budget-row">
              <div class="f1-budget-name">{{ b.name }}</div>
              <div class="f1-budget-track">
                <div class="f1-budget-fill" :style="{ width: Math.min(b.pct, 100) + '%', background: b.over ? '#ef4444' : f1CatColor(b.catId) }"></div>
              </div>
              <div class="f1-budget-vals">
                <span :class="b.over ? 'f1-budget-over' : 'f1-budget-ok'">{{ f1FormatAmt(b.actual) }}</span>
                <span class="f1-budget-sep">/</span>
                <span class="f1-budget-limit">{{ f1FormatAmt(b.budget) }}</span>
                <span class="f1-budget-pct" :class="b.over ? 'f1-budget-over' : 'f1-budget-ok'">{{ b.pct }}%</span>
              </div>
            </div>
          </div>
        </div>

        <div class="c1-nm-div"></div>
        <c1-mod-calendar :items="f1Transactions" dateField="created_at" itemLabel="transactions" style="flex-shrink:0" />

      </div>
    </div>
    <!-- /TOP STRIP -->

    <!-- PANEL BACKDROP -->
    <transition name="c1-bd-fade">
      <div v-if="c1SlideOpen" class="c1-panel-backdrop" @click.stop="c1SlideOpen = null"></div>
    </transition>

    <!-- UNIFIED PANEL -->
    <div class="c1-panel" :class="{ 'c1-panel--open': c1PanelIsOpen }" @click.capture="c1FilterPanelClick">
      <div class="c1-panel-track" :class="{ 'at-content': c1PanelAtContent }">

        <!-- SCREEN 0: NAV -->
        <div class="c1-panel-screen">

          <!-- ── USER STRIP ── -->
          <div class="c1-nav-user-strip">
            <div class="c1-nav-av">
              {{ c1UserInitials }}
              <span class="c1-nav-av-dot" :class="{ on: f1UserOnline }"></span>
            </div>
            <div class="c1-nav-uinfo">
              <div class="c1-nav-uname">{{ c1UserFullName }}</div>
              <div class="c1-nav-urole">{{ c1RoleLabel }}</div>
            </div>
            <button class="c1-nav-online-btn" :class="{ on: f1UserOnline }" @click.stop="f1UserOnline = !f1UserOnline" title="Toggle online">
              <span class="c1-nav-online-knob"></span>
            </button>
            <button class="c1-nav-logout-btn" @click.stop="c1Logout()" title="Log out">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:14px;height:14px"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            </button>
          </div>

          <!-- ── TIMEZONE BAR ── -->
          <div class="c1-nav-tz-bar">
            <div class="c1-nav-tz-cell"><span class="c1-nav-tz-flag">🇬🇧</span><span class="c1-nav-tz-time">{{ c1TzUK }}</span></div>
            <div class="c1-nav-tz-sep"></div>
            <div class="c1-nav-tz-cell"><span class="c1-nav-tz-flag">🇮🇱</span><span class="c1-nav-tz-time">{{ c1TzIL }}</span></div>
            <div class="c1-nav-tz-sep"></div>
            <div class="c1-nav-tz-cell"><span class="c1-nav-tz-flag">🏝</span><span class="c1-nav-tz-time">{{ c1TzCB }}</span></div>
          </div>

          <!-- ── NAVIGATE: all pages ── -->
          <div v-if="c1NavMode === 'navigate'" class="c1-sl-body" style="padding:6px 0 12px">
            <div class="c1-psel-sub c1-psel-sub-back" @click.stop="c1NavMode = 'panels'">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width:14px;height:14px;flex-shrink:0"><polyline points="15 18 9 12 15 6"/></svg>
              <span class="c1-psel-sub-lbl">{{ c1CurrentPageLabel }}</span>
            <span class="c1-nav-x-btn" @click.stop="c1SlideOpen = null"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width:15px;height:15px;display:block"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></span></div>
            <div class="c1-psel-divider"></div>
            <div class="c1-psel-grp-hdr">CRM</div>
            <router-link class="c1-psel-sub" to="/clients1" @click.native="c1SlideOpen = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span><span class="c1-psel-sub-lbl">Clients</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/tasks1" @click.native="c1SlideOpen = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span><span class="c1-psel-sub-lbl">Tasks</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/stats1" @click.native="c1SlideOpen = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span><span class="c1-psel-sub-lbl">Statistics</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/chat1" @click.native="c1SlideOpen = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span><span class="c1-psel-sub-lbl">Chat</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/communications1" @click.native="c1SlideOpen = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span><span class="c1-psel-sub-lbl">Communications</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/affiliation1" @click.native="c1SlideOpen = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span><span class="c1-psel-sub-lbl">Affiliation</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <div class="c1-psel-divider"></div>
            <div class="c1-psel-grp-hdr">Sales</div>
            <router-link class="c1-psel-sub" to="/finance1" @click.native="c1SlideOpen = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-sc)"></span><span class="c1-psel-sub-lbl">Finance</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/shifts1" @click.native="c1SlideOpen = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-sc)"></span><span class="c1-psel-sub-lbl">Shifts</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/games1" @click.native="c1SlideOpen = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-sc)"></span><span class="c1-psel-sub-lbl">Games</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <div class="c1-psel-divider"></div>
            <div class="c1-psel-grp-hdr">Team</div>
            <router-link class="c1-psel-sub" to="/users1" @click.native="c1SlideOpen = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-rf)"></span><span class="c1-psel-sub-lbl">Users</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/brokers1" @click.native="c1SlideOpen = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-rf)"></span><span class="c1-psel-sub-lbl">Brokers</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/security1" @click.native="c1SlideOpen = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-rf)"></span><span class="c1-psel-sub-lbl">Security</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <div class="c1-psel-divider"></div>
            <div class="c1-psel-grp-hdr">Admin</div>
            <router-link class="c1-psel-sub" to="/quiz1" @click.native="c1SlideOpen = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:#7c6f9f"></span><span class="c1-psel-sub-lbl">Quiz</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/instruction1" @click.native="c1SlideOpen = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:#7c6f9f"></span><span class="c1-psel-sub-lbl">Instructions</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/settings1" @click.native="c1SlideOpen = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:#7c6f9f"></span><span class="c1-psel-sub-lbl">Settings</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/profile1" @click.native="c1SlideOpen = null; sessionStorage.setItem('c1keepNav','1')"><span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span><span class="c1-psel-sub-lbl">My Profile</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
          </div>

          <!-- ── PANELS MODE ── -->
          <div v-else class="c1-sl-body" style="padding:6px 0 12px">

            <!-- Navigate row -->
            <div class="c1-psel-sub c1-psel-sub-navigate" @click.stop="c1NavMode = 'navigate'">
              <span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span>
              <span class="c1-psel-sub-lbl" style="font-weight:600;color:var(--c1-dp)">{{ c1CurrentPageLabel }}</span>
              <span class="c1-psel-sub-hint">Go to another page</span>
              <svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
            <div class="c1-psel-divider"></div>

            <div class="c1-psel-sub" @click="c1SelectPanel('filters')">
              <span class="c1-psel-sub-dot">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:13px;height:13px"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
              </span>
              <span class="c1-psel-sub-lbl">Filters</span>
              <span v-if="f1ActiveFilterCount" class="c1-psel-sub-hint">{{ f1ActiveFilterCount }} active</span>
              <svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
            <div class="c1-psel-sub" @click="c1SelectPanel('charts')">
              <span class="c1-psel-sub-dot">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:13px;height:13px"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
              </span>
              <span class="c1-psel-sub-lbl">Analytics</span>
              <svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
            <div class="c1-psel-sub" @click="c1SelectPanel('calendar')">
              <span class="c1-psel-sub-dot">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:13px;height:13px"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              </span>
              <span class="c1-psel-sub-lbl">Calendar</span>
              <svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
            <div class="c1-psel-sub" @click="c1SelectPanel('viki')">
              <span class="c1-psel-sub-dot">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:13px;height:13px"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
              </span>
              <span class="c1-psel-sub-lbl">Viki AI</span>
              <span v-if="f1VikiAlerts.length" class="c1-psel-sub-hint">{{ f1VikiAlerts.length }} alerts</span>
              <svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
          </div><!-- end v-else panels -->
        </div>

        <!-- SCREEN 1: CONTENT -->
        <div class="c1-panel-screen">
          <div class="c1-panel-hdr-bar">
            <div class="c1-panel-back-btn" @click="c1SlideOpen = 'nav'">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </div>
            <span class="c1-panel-hdr-ttl">{{ c1ActivePanelLabel }}</span>
            <div class="c1-panel-back-btn" @click="c1SlideOpen = null">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </div>
          </div>
          <div class="c1-sl-body">

            <!-- ═══ FILTERS CONTENT ═══ -->
            <template v-if="c1PanelActiveContent === 'filters'">

              <div v-if="f1ActiveFilterCount" class="c1-reset-bar" @click="f1ResetFilters">
                <svg viewBox="0 0 24 24"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.95"/></svg>
                Reset all filters
                <span class="c1-reset-bar-count">{{ f1ActiveFilterCount }}</span>
              </div>

              <!-- Date range presets -->
              <div class="c1-fp-group c1-fp-full">
                <div class="c1-fp-lbl">Date range</div>
                <div class="c1-fp-opts">
                  <div class="c1-fp-opt" :class="{ on: f1DateFilter === 'today' }"     @click="f1ApplyDateFilter('today')">T</div>
                  <div class="c1-fp-opt" :class="{ on: f1DateFilter === 'yesterday' }" @click="f1ApplyDateFilter('yesterday')">Y</div>
                  <div class="c1-fp-opt" :class="{ on: f1DateFilter === 'week' }"      @click="f1ApplyDateFilter('week')">TW</div>
                  <div class="c1-fp-opt" :class="{ on: f1DateFilter === 'lastweek' }"  @click="f1ApplyDateFilter('lastweek')">LW</div>
                  <div class="c1-fp-opt" :class="{ on: f1DateFilter === 'month' }"     @click="f1ApplyDateFilter('month')">TM</div>
                  <div class="c1-fp-opt" :class="{ on: f1DateFilter === 'lastmonth' }" @click="f1ApplyDateFilter('lastmonth')">LM</div>
                  <div class="c1-fp-opt" :class="{ on: f1DateFilter === 'all' }"       @click="f1ApplyDateFilter('all')">ALL</div>
                </div>
                <div class="c1-date-range-row" style="margin-top:8px">
                  <input type="date" class="c1-date-input" v-model="f1Filters.date_from" @change="f1DateFilter='custom'; f1Fetch()" />
                  <span class="c1-date-sep">→</span>
                  <input type="date" class="c1-date-input" v-model="f1Filters.date_to"   @change="f1DateFilter='custom'; f1Fetch()" />
                </div>
              </div>

              <!-- Category multi-select -->
              <div class="c1-fp-group c1-fp-full">
                <div class="c1-fp-lbl">Category</div>
                <div class="c1-fp-opts" style="flex-wrap:wrap">
                  <div class="c1-fp-opt" :class="{ on: !f1Filters.category_ids.length }" @click="f1Filters.category_ids = []; f1Fetch()">All</div>
                  <div
                    v-for="cat in f1Categories"
                    :key="cat.id"
                    class="c1-fp-opt"
                    :class="{ on: f1Filters.category_ids.includes(cat.id) }"
                    :style="f1Filters.category_ids.includes(cat.id) ? { background: f1CatColor(cat.id), color: '#fff', borderColor: f1CatColor(cat.id) } : {}"
                    @click="f1ToggleCategoryFilter(cat.id)"
                  >{{ cat.name }}</div>
                </div>
              </div>

              <!-- Amount range -->
              <div class="c1-fp-group c1-fp-full">
                <div class="c1-fp-lbl">Amount range</div>
                <div class="f1-range-row">
                  <input type="number" class="c1-pkf-input f1-range-input" v-model.number="f1Filters.amount_min" placeholder="Min $" @change="f1Fetch()" />
                  <span class="c1-date-sep">—</span>
                  <input type="number" class="c1-pkf-input f1-range-input" v-model.number="f1Filters.amount_max" placeholder="Max $" @change="f1Fetch()" />
                </div>
              </div>

              <!-- User who added -->
              <div class="c1-fp-group c1-fp-full">
                <div class="c1-fp-lbl">Added by</div>
                <div class="c1-fp-opts" style="flex-wrap:wrap">
                  <div class="c1-fp-opt" :class="{ on: !f1Filters.user_id }" @click="f1Filters.user_id = null; f1Fetch()">All</div>
                  <div
                    v-for="u in f1UserList"
                    :key="u.id"
                    class="c1-fp-opt"
                    :class="{ on: f1Filters.user_id === u.id }"
                    @click="f1Filters.user_id = u.id; f1Fetch()"
                  >{{ u.first_name }} {{ (u.last_name || '')[0] || '' }}.</div>
                </div>
              </div>

              <!-- Payment method -->
              <div class="c1-fp-group c1-fp-full">
                <div class="c1-fp-lbl">Payment method</div>
                <div class="c1-fp-opts">
                  <div class="c1-fp-opt" :class="{ on: !f1Filters.payment_method }" @click="f1Filters.payment_method = null; f1Fetch()">All</div>
                  <div v-for="m in f1PaymentMethods" :key="m" class="c1-fp-opt" :class="{ on: f1Filters.payment_method === m }" @click="f1Filters.payment_method = m; f1Fetch()">{{ m }}</div>
                </div>
              </div>

              <!-- Status -->
              <div class="c1-fp-group c1-fp-full">
                <div class="c1-fp-lbl">Status</div>
                <div class="c1-fp-opts">
                  <div class="c1-fp-opt" :class="{ on: !f1Filters.status }" @click="f1Filters.status = null; f1Fetch()">All</div>
                  <div class="c1-fp-opt f1-status-approved" :class="{ on: f1Filters.status === 'approved' }" @click="f1Filters.status = 'approved'; f1Fetch()">Approved</div>
                  <div class="c1-fp-opt f1-status-pending"  :class="{ on: f1Filters.status === 'pending' }"  @click="f1Filters.status = 'pending'; f1Fetch()">Pending</div>
                  <div class="c1-fp-opt f1-status-rejected" :class="{ on: f1Filters.status === 'rejected' }" @click="f1Filters.status = 'rejected'; f1Fetch()">Rejected</div>
                </div>
              </div>

              <!-- Search -->
              <div class="c1-fp-group c1-fp-full">
                <div class="c1-fp-lbl">Search</div>
                <div class="c1-fp-search">
                  <svg class="c1-fp-sico" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <input class="c1-fp-sinput" v-model.trim="f1Filters.search" placeholder="Description, amount…" type="text" @input="f1DebouncedFetch" />
                  <div v-if="f1Filters.search" class="c1-fp-sclear" @click="f1Filters.search = ''; f1Fetch()">
                    <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </div>
                </div>
              </div>

            </template>

            <!-- ═══ ANALYTICS CONTENT ═══ -->
            <template v-if="c1PanelActiveContent === 'charts'">
      <div class="c1-sl-body">

        <!-- By Team comparison bar chart -->
        <div class="c1-co-lbl">Team 1 vs Team 2</div>
        <div v-if="!f1HasUserTeamData()" class="f1-team-chart-nodata">
          Team-level filtering requires finance records to be user-linked. Contact admin.
        </div>
        <div v-else class="f1-team-bar-chart">
          <div class="f1-tbc-col">
            <div class="f1-tbc-track">
              <div class="f1-tbc-fill f1-tbc-t1" :style="{ height: f1TeamBarPct(1) + '%' }"></div>
            </div>
            <div class="f1-tbc-lbl">⚡ Team 1</div>
            <div class="f1-tbc-val">{{ f1TeamSpend(1) !== null ? f1FormatAmt(f1TeamSpend(1)) : '—' }}</div>
          </div>
          <div class="f1-tbc-col">
            <div class="f1-tbc-track">
              <div class="f1-tbc-fill f1-tbc-t2" :style="{ height: f1TeamBarPct(2) + '%' }"></div>
            </div>
            <div class="f1-tbc-lbl">🔥 Team 2</div>
            <div class="f1-tbc-val">{{ f1TeamSpend(2) !== null ? f1FormatAmt(f1TeamSpend(2)) : '—' }}</div>
          </div>
        </div>

        <!-- Donut: spend by category -->
        <div class="c1-co-lbl" style="margin-top:18px">Spend by category</div>
        <div class="f1-donut-wrap">
          <svg class="f1-donut-svg" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="14" fill="none" stroke="#e4e3df" stroke-width="4"/>
            <circle
              v-for="(seg, si) in f1DonutSegments"
              :key="si"
              cx="18" cy="18" r="14" fill="none"
              :stroke="seg.color" stroke-width="4"
              :stroke-dasharray="seg.dash + ' ' + (88 - seg.dash)"
              :stroke-dashoffset="'-' + seg.offset"
              stroke-linecap="round"
            />
          </svg>
          <div class="f1-donut-center">
            <div class="f1-donut-total">{{ f1FormatAmt(f1SummaryTotal) }}</div>
            <div class="f1-donut-lbl">total</div>
          </div>
        </div>
        <div class="f1-donut-legend">
          <div v-for="(seg, si) in f1DonutSegments" :key="'leg-'+si" class="f1-leg-row">
            <div class="f1-leg-dot" :style="{ background: seg.color }"></div>
            <span class="f1-leg-name">{{ seg.name }}</span>
            <span class="f1-leg-val">{{ f1FormatAmt(seg.amount) }}</span>
            <span class="f1-leg-pct">{{ seg.pct }}%</span>
          </div>
        </div>

        <!-- Line chart: daily spend last 30 days -->
        <div class="c1-co-lbl" style="margin-top:18px">Daily spend — last 30 days</div>
        <div class="f1-line-wrap">
          <svg class="f1-line-svg" viewBox="0 0 300 80" preserveAspectRatio="none">
            <defs>
              <linearGradient id="f1lg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#6366f1" stop-opacity="0.4"/>
                <stop offset="100%" stop-color="#6366f1" stop-opacity="0"/>
              </linearGradient>
            </defs>
            <path v-if="f1LineArea" :d="f1LineArea" fill="url(#f1lg)"/>
            <path v-if="f1LinePath" :d="f1LinePath" fill="none" stroke="#6366f1" stroke-width="1.5" stroke-linejoin="round"/>
            <circle
              v-for="(pt, pi) in f1LinePoints"
              :key="'lp'+pi"
              :cx="pt.x" :cy="pt.y" r="2"
              fill="#6366f1"
            />
          </svg>
          <div class="f1-line-labels">
            <span v-if="f1DailyBars.length">{{ f1DailyBars[0] && f1DailyBars[0].label }}</span>
            <span class="c1-hp"></span>
            <span v-if="f1DailyBars.length">{{ f1DailyBars[f1DailyBars.length-1] && f1DailyBars[f1DailyBars.length-1].label }}</span>
          </div>
        </div>

        <!-- Budget vs Actual bar chart -->
        <div class="c1-co-lbl" style="margin-top:18px">Budget vs Actual</div>
        <div class="f1-budget-rows">
          <div v-for="b in f1BudgetRows" :key="b.name" class="f1-budget-row">
            <div class="f1-budget-name">{{ b.name }}</div>
            <div class="f1-budget-track">
              <div class="f1-budget-fill" :style="{ width: Math.min(b.pct, 100) + '%', background: b.over ? '#ef4444' : f1CatColor(b.catId) }"></div>
            </div>
            <div class="f1-budget-vals">
              <span :class="b.over ? 'f1-budget-over' : 'f1-budget-ok'">{{ f1FormatAmt(b.actual) }}</span>
              <span class="f1-budget-sep">/</span>
              <span class="f1-budget-limit">{{ f1FormatAmt(b.budget) }}</span>
              <span class="f1-budget-pct" :class="b.over ? 'f1-budget-over' : 'f1-budget-ok'">{{ b.pct }}%</span>
            </div>
          </div>
        </div>

        <!-- Per-sector profitability -->
        <div class="c1-co-lbl" style="margin-top:18px">Campaign Profitability</div>
        <div v-if="f1ProfitRows.length === 0" class="f1-profit-empty">No campaign data for this period</div>
        <div v-for="pr in f1ProfitRows" :key="pr.name" class="f1-profit-card">
          <div class="f1-profit-name">{{ pr.name }}</div>
          <div class="f1-profit-row">
            <div class="f1-profit-kpi">
              <div class="f1-profit-kv f1-pk-spend">{{ f1FormatAmt(pr.cost) }}</div>
              <div class="f1-profit-kl">Spent</div>
            </div>
            <div class="f1-profit-kpi">
              <div class="f1-profit-kv f1-pk-rev">{{ f1FormatAmt(pr.revenue) }}</div>
              <div class="f1-profit-kl">Revenue</div>
            </div>
            <div class="f1-profit-kpi">
              <div class="f1-profit-kv" :class="pr.profit >= 0 ? 'f1-pk-profit' : 'f1-pk-loss'">{{ f1FormatAmt(pr.profit) }}</div>
              <div class="f1-profit-kl">Profit</div>
            </div>
            <div class="f1-profit-kpi">
              <div class="f1-profit-kv" :class="pr.roi >= 0 ? 'f1-pk-profit' : 'f1-pk-loss'">{{ pr.roi > 0 ? '+' : '' }}{{ pr.roi }}%</div>
              <div class="f1-profit-kl">ROI</div>
            </div>
          </div>
          <div class="f1-profit-bar-track">
            <div class="f1-profit-bar-fill" :style="{ width: Math.min(Math.abs(pr.roi), 200) / 2 + '%', background: pr.roi >= 0 ? '#4ade80' : '#f87171' }"></div>
          </div>
          <div class="f1-profit-detail">CPL: {{ f1FormatAmt(pr.cpl) }} · Calls: {{ pr.calls }} · Call cost: {{ f1FormatAmt(pr.call_cost) }}</div>
        </div>

        <!-- MRR / Salary section -->
        <div class="c1-co-lbl" style="margin-top:18px">Monthly Recurring Revenue</div>
        <div class="f1-mrr-grid">
          <div v-for="plan in f1MRRPlans" :key="plan.name" class="f1-mrr-card">
            <div class="f1-mrr-plan">{{ plan.name }}</div>
            <div class="f1-mrr-price">${{ plan.price }}/mo</div>
            <div class="f1-mrr-count">{{ plan.count }} clients</div>
            <div class="f1-mrr-total">{{ f1FormatAmt(plan.price * plan.count) }}</div>
          </div>
        </div>
        <div class="f1-mrr-total-row">
          <span class="f1-mrr-total-lbl">Total MRR</span>
          <span class="f1-mrr-total-val">{{ f1FormatAmt(f1TotalMRR) }}</span>
        </div>

      </div><!-- /c1-sl-body for charts -->
            </template>

            <!-- ═══ CALENDAR CONTENT ═══ -->
            <template v-if="c1PanelActiveContent === 'calendar'">
              <div class="f1-cal-nav">
                <button class="f1-cal-nav-btn" @click="f1CalPrev">
                  <svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <div class="f1-cal-title">{{ f1CalMonthNames[f1Cal.month] }} {{ f1Cal.year }}</div>
                <button class="f1-cal-nav-btn" @click="f1CalNext">
                  <svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              </div>
              <div class="f1-cal-grid">
                <div class="f1-cal-dow" v-for="(d, di) in ['M','T','W','T','F','S','S']" :key="'dow'+di">{{ d }}</div>
                <div
                  v-for="cell in f1CalGrid"
                  :key="cell.date"
                  class="f1-cal-cell"
                  :class="{
                    'out': !cell.inMonth,
                    'today': cell.today,
                    'selected': f1Filters.date === cell.date,
                    'has-tx': cell.count > 0,
                    'cal-over': cell.overBudget,
                    'cal-ok': cell.count > 0 && !cell.overBudget
                  }"
                  @click="f1CalDayClick(cell)"
                >
                  <span class="f1-cal-day">{{ cell.day }}</span>
                  <span v-if="cell.count > 0" class="f1-cal-dot" :class="{ 'dot-over': cell.overBudget }"></span>
                  <span v-if="cell.count > 0 && cell.total" class="f1-cal-amt">{{ f1FormatAmtShort(cell.total) }}</span>
                </div>
              </div>
              <div v-if="f1Filters.date" class="f1-cal-day-summary">
                <div class="f1-cds-label">{{ f1Filters.date }}</div>
                <div class="f1-cds-total">{{ f1CalDayTotal }}</div>
                <div class="f1-cds-count">{{ f1CalDayCount }} transaction{{ f1CalDayCount !== 1 ? 's' : '' }}</div>
                <button class="f1-cds-clear" @click="f1Filters.date = null; f1Fetch()">Clear day filter</button>
              </div>
              <!-- Legend -->
              <div class="f1-cal-legend">
                <span class="f1-cal-leg-item"><span class="f1-cal-leg-dot" style="background:#4ade80"></span>Under budget</span>
                <span class="f1-cal-leg-item"><span class="f1-cal-leg-dot" style="background:#f87171"></span>Over budget</span>
              </div>
            </template>

            <!-- ═══ VIKI CONTENT ═══ -->
            <template v-if="c1PanelActiveContent === 'viki'">

              <!-- Today budget status -->
              <div class="f1-viki-budget-box">
                <div class="f1-viki-budget-title">Today's Budget Status</div>
                <div class="f1-viki-budget-row">
                  <div class="f1-viki-spent">{{ f1FormatAmt(f1TodaySpend) }}</div>
                  <div class="f1-viki-budget-sep">of</div>
                  <div class="f1-viki-budget-limit">{{ f1FormatAmt(f1DailyBudgetLimit) }}</div>
                </div>
                <div class="f1-viki-budget-track">
                  <div class="f1-viki-budget-fill" :style="{ width: Math.min(f1TodayBudgetPct, 100) + '%', background: f1TodayBudgetPct > 100 ? '#ef4444' : f1TodayBudgetPct > 80 ? '#f59e0b' : '#4ade80' }"></div>
                </div>
                <div class="f1-viki-budget-pct" :class="f1TodayBudgetPct > 100 ? 'f1-over' : 'f1-ok'">{{ f1TodayBudgetPct }}% used today</div>
              </div>

              <!-- Anomaly alerts -->
              <div class="f1-viki-section-lbl">Anomaly Detection</div>
              <div v-if="f1VikiAlerts.length === 0" class="f1-viki-noalerts">
                <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                All spending looks normal
              </div>
              <div v-for="alert in f1VikiAlerts" :key="alert.id" class="f1-viki-alert" :class="'f1-viki-' + alert.level">
                <div class="f1-viki-alert-icon">
                  <svg v-if="alert.level === 'warn'" viewBox="0 0 24 24"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  <svg v-else viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                </div>
                <div class="f1-viki-alert-body">
                  <div class="f1-viki-alert-msg">{{ alert.msg }}</div>
                  <div class="f1-viki-alert-meta">{{ alert.meta }}</div>
                </div>
              </div>

              <!-- Top spends today -->
              <div class="f1-viki-section-lbl" style="margin-top:16px">Top Expenses Today</div>
              <div v-if="f1TodayTopTx.length === 0" class="f1-viki-none">No transactions today</div>
              <div v-for="tx in f1TodayTopTx" :key="'vtx'+tx.id" class="f1-viki-tx-row" @click="f1OpenDetailSlide(tx)">
                <div class="f1-cat-chip" :style="{ background: f1CatBg(tx.category_id), color: f1CatColor(tx.category_id) }">{{ tx.category_name || '?' }}</div>
                <div class="f1-viki-tx-desc">{{ tx.description || 'Transaction' }}</div>
                <div class="f1-viki-tx-amt expense">-{{ f1FormatAmt(tx.amount) }}</div>
              </div>

              <!-- Weekly breakdown -->
              <div class="f1-viki-section-lbl" style="margin-top:16px">This Week by Category</div>
              <div v-for="item in f1WeekCatBreakdown" :key="'wkc'+item.name" class="f1-viki-week-row">
                <div class="f1-leg-dot" :style="{ background: item.color }"></div>
                <span class="f1-viki-wk-name">{{ item.name }}</span>
                <div class="f1-viki-wk-track">
                  <div class="f1-viki-wk-fill" :style="{ width: item.pct + '%', background: item.color }"></div>
                </div>
                <span class="f1-viki-wk-val">{{ f1FormatAmt(item.amount) }}</span>
              </div>

            </template>

          </div>
        </div>

      </div>
    </div>

    <!-- ═══ TEAM SCOPE TOGGLE ═══ -->
    <div class="f1-team-scope-bar">
      <div class="f1-ts-lbl">View by:</div>
      <div class="f1-ts-btns">
        <div class="f1-ts-btn" :class="{ on: f1TeamScope === null }" @click="f1SetTeamScope(null)">Company-wide</div>
        <div class="f1-ts-btn f1-ts-btn-t1" :class="{ on: f1TeamScope === 1 }" @click="f1SetTeamScope(1)">⚡ Team 1</div>
        <div class="f1-ts-btn f1-ts-btn-t2" :class="{ on: f1TeamScope === 2 }" @click="f1SetTeamScope(2)">🔥 Team 2</div>
      </div>
    </div>

    <!-- ═══ SUMMARY BAR ═══ -->
    <div class="f1-summary-bar">
      <div class="f1-kpi">
        <div class="f1-kpi-val">{{ f1FormatAmt(f1SummaryTotal) }}</div>
        <div class="f1-kpi-lbl">Total</div>
      </div>
      <div class="f1-kpi-sep"></div>
      <div class="f1-kpi">
        <div class="f1-kpi-val">{{ f1Pagination.total }}</div>
        <div class="f1-kpi-lbl">Transactions</div>
      </div>
      <div class="f1-kpi-sep"></div>
      <div class="f1-kpi">
        <div class="f1-kpi-val">{{ f1FormatAmt(f1AvgPerDay) }}</div>
        <div class="f1-kpi-lbl">Avg/day</div>
      </div>
      <div class="f1-kpi-sep"></div>
      <div class="f1-kpi">
        <div class="f1-kpi-val f1-kpi-income">{{ f1FormatAmt(f1SummaryIncome) }}</div>
        <div class="f1-kpi-lbl">Income</div>
      </div>
      <div class="f1-kpi-sep"></div>
      <div class="f1-kpi">
        <div class="f1-kpi-val f1-kpi-expense">{{ f1FormatAmt(f1SummaryExpense) }}</div>
        <div class="f1-kpi-lbl">Expense</div>
      </div>
      <div class="f1-kpi-sep"></div>
      <div class="f1-kpi">
        <div class="f1-kpi-val" :class="f1NetBalance >= 0 ? 'f1-kpi-income' : 'f1-kpi-expense'">{{ f1FormatAmt(f1NetBalance) }}</div>
        <div class="f1-kpi-lbl">Net</div>
      </div>
    </div>

    <!-- ═══ TEAM BUDGET COMPARISON CARDS ═══ -->
    <div v-if="f1TeamScope === null" class="f1-team-compare-row">
      <!-- Company-wide: show both teams side by side -->
      <template v-if="f1HasUserTeamData()">
        <div class="f1-team-card f1-team-card-t1">
          <div class="f1-team-card-label">⚡ Team 1 Spend This Month</div>
          <div class="f1-team-card-amount">{{ f1TeamSpend(1) !== null ? f1FormatAmt(f1TeamSpend(1)) : '—' }}</div>
        </div>
        <div class="f1-team-vs">vs</div>
        <div class="f1-team-card f1-team-card-t2">
          <div class="f1-team-card-label">🔥 Team 2 Spend This Month</div>
          <div class="f1-team-card-amount">{{ f1TeamSpend(2) !== null ? f1FormatAmt(f1TeamSpend(2)) : '—' }}</div>
        </div>
      </template>
      <div v-else class="f1-team-nolink">
        Team-level filtering requires finance records to be user-linked. Contact admin.
      </div>
    </div>
    <div v-else-if="f1TeamScope" class="f1-team-compare-row f1-team-compare-single">
      <div class="f1-team-card" :class="f1TeamScope === 1 ? 'f1-team-card-t1' : 'f1-team-card-t2'">
        <div class="f1-team-card-label">{{ f1TeamScope === 1 ? '⚡ Team 1' : '🔥 Team 2' }} Spend This Month</div>
        <div class="f1-team-card-amount">
          <template v-if="f1HasUserTeamData()">{{ f1TeamSpend(f1TeamScope) !== null ? f1FormatAmt(f1TeamSpend(f1TeamScope)) : '—' }}</template>
          <template v-else><span class="f1-team-nolink-sm">Requires user-linking</span></template>
        </div>
      </div>
    </div>

    <!-- ═══ MAIN CONTENT ═══ -->
    <div class="f1-list" v-if="!f1Loading">

      <!-- Empty state -->
      <div v-if="f1Transactions.length === 0" class="c1-empty">
        <div class="c1-empty-icon">
          <svg viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
        </div>
        <div class="c1-empty-msg">No transactions found</div>
        <div class="f1-empty-sub">Try adjusting the filters or add a new transaction</div>
      </div>

      <!-- Transaction rows -->
      <div
        v-for="tx in f1Transactions"
        :key="tx.id"
        class="f1-tx-box"
        :class="{ expanded: f1ExpandedId === tx.id }"
      >
        <!-- COLLAPSED ROW -->
        <div class="f1-tx-row" @click="f1ToggleExpand(tx.id)">
          <div class="f1-tx-date">
            <div class="f1-tx-day">{{ f1DayLabel(tx.created_at) }}</div>
            <div class="f1-tx-mon">{{ f1MonLabel(tx.created_at) }}</div>
          </div>
          <div class="f1-tx-mid">
            <div class="f1-tx-desc">{{ tx.description || tx.category_name || 'Transaction' }}</div>
            <div class="f1-tx-meta">
              <span class="f1-cat-chip" :style="{ background: f1CatBg(tx.category_id), color: f1CatColor(tx.category_id) }">{{ tx.category_name || 'Uncategorized' }}</span>
              <span v-if="tx.project_name" class="f1-tx-proj">{{ tx.project_name }}</span>
              <span v-if="tx.recurring && tx.recurring !== 'one-time'" class="f1-recur-badge">
                <svg viewBox="0 0 24 24"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.95"/></svg>
                {{ tx.recurring }}
              </span>
            </div>
          </div>
          <div class="f1-tx-right">
            <div class="f1-tx-currency-row">
              <span class="f1-tx-currency-badge">{{ f1CurrencySymbol(tx.currency) }}</span>
              <div class="f1-tx-amount" :class="tx.type === 'income' ? 'income' : 'expense'">
                {{ tx.type === 'income' ? '+' : '-' }}{{ f1FormatAmtCurrency(tx.amount, tx.currency) }}
              </div>
            </div>
            <div class="f1-tx-status-row">
              <span class="f1-status-chip" :class="'f1-status-' + (tx.status || 'pending')">{{ tx.status || 'pending' }}</span>
              <div class="f1-tx-av" :style="{ background: f1UserAvatarColor(tx.user_id) }">{{ f1TxUserInitials(tx) }}</div>
            </div>
          </div>
          <svg class="f1-tx-chv" :class="{ open: f1ExpandedId === tx.id }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
        </div>

        <!-- EXPANDED DETAIL -->
        <div v-if="f1ExpandedId === tx.id" class="f1-tx-detail">
          <div class="f1-td-grid">
            <div class="f1-td-field">
              <div class="f1-td-lbl">Amount</div>
              <div class="f1-td-val f1-td-big" :class="tx.type === 'income' ? 'income' : 'expense'">
                {{ tx.type === 'income' ? '+' : '-' }}{{ f1FormatAmtCurrency(tx.amount, tx.currency) }}
              </div>
            </div>
            <div class="f1-td-field">
              <div class="f1-td-lbl">Currency</div>
              <div class="f1-td-val">
                <span class="f1-currency-chip">{{ tx.currency || 'USD' }}</span>
              </div>
            </div>
            <div class="f1-td-field">
              <div class="f1-td-lbl">Date</div>
              <div class="f1-td-val f1-mono">{{ f1FmtDate(tx.created_at) }}</div>
            </div>
            <div class="f1-td-field">
              <div class="f1-td-lbl">Category</div>
              <div class="f1-td-val">
                <span class="f1-cat-chip" :style="{ background: f1CatBg(tx.category_id), color: f1CatColor(tx.category_id) }">{{ tx.category_name || '—' }}</span>
              </div>
            </div>
            <div class="f1-td-field">
              <div class="f1-td-lbl">Status</div>
              <div class="f1-td-val">
                <span class="f1-status-chip" :class="'f1-status-' + (tx.status || 'pending')">{{ tx.status || 'pending' }}</span>
              </div>
            </div>
            <div v-if="tx.payment_method" class="f1-td-field">
              <div class="f1-td-lbl">Payment</div>
              <div class="f1-td-val">{{ tx.payment_method }}</div>
            </div>
            <div v-if="tx.recurring" class="f1-td-field">
              <div class="f1-td-lbl">Recurring</div>
              <div class="f1-td-val">{{ tx.recurring }}</div>
            </div>
            <div v-if="tx.project_name" class="f1-td-field">
              <div class="f1-td-lbl">Project</div>
              <div class="f1-td-val">{{ tx.project_name }}</div>
            </div>
            <div v-if="tx.user_name" class="f1-td-field">
              <div class="f1-td-lbl">Added by</div>
              <div class="f1-td-val">{{ tx.user_name }}</div>
            </div>
            <div v-if="tx.approved_by" class="f1-td-field">
              <div class="f1-td-lbl">Approved by</div>
              <div class="f1-td-val">{{ tx.approved_by }}</div>
            </div>
          </div>
          <div v-if="tx.description" class="f1-td-field f1-td-full" style="margin-top:8px">
            <div class="f1-td-lbl">Description</div>
            <div class="f1-td-val f1-td-note">{{ tx.description }}</div>
          </div>

          <!-- Receipt image slot -->
          <div v-if="tx.receipt_url" class="f1-td-receipt-slot">
            <img :src="tx.receipt_url" alt="Receipt" class="f1-receipt-thumb" @click.stop="f1OpenReceipt(tx.receipt_url)" />
            <div class="f1-receipt-hint">Tap to view full receipt</div>
          </div>

          <!-- Approval history -->
          <div v-if="tx.approval_history && tx.approval_history.length" class="f1-td-history">
            <div class="f1-td-hist-lbl">Approval history</div>
            <div v-for="(h, hi) in tx.approval_history" :key="hi" class="f1-td-hist-row">
              <span class="f1-status-chip" :class="'f1-status-' + h.status">{{ h.status }}</span>
              <span class="f1-td-hist-by">{{ h.by }}</span>
              <span class="f1-td-hist-dt f1-mono">{{ f1FmtDate(h.date) }}</span>
            </div>
          </div>

          <div class="f1-td-actions">
            <button v-if="tx.receipt_url" class="f1-td-btn f1-td-btn-receipt" @click.stop="f1OpenReceipt(tx.receipt_url)">
              <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              Receipt
            </button>
            <button class="f1-td-btn f1-td-btn-detail" @click.stop="f1OpenDetailSlide(tx)">
              <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              Detail
            </button>
            <button v-if="f1IsAdmin" class="f1-td-btn f1-td-btn-edit" @click.stop="f1OpenDetailSlide(tx)">
              <svg viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Edit
            </button>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div class="c1-pg-bar" v-if="f1Pagination.lastPage > 1">
        <div class="c1-pg-btn" :class="{ disabled: f1Pagination.currentPage <= 1 }" @click="f1GoPage(1)">
          <svg viewBox="0 0 24 24"><polyline points="11 17 6 12 11 7"/><polyline points="18 17 13 12 18 7"/></svg>
        </div>
        <div class="c1-pg-btn" :class="{ disabled: f1Pagination.currentPage <= 1 }" @click="f1GoPage(f1Pagination.currentPage - 1)">
          <svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
        </div>
        <div class="c1-pg-info">{{ f1Pagination.from }}–{{ f1Pagination.to }} of {{ f1Pagination.total }}</div>
        <div class="c1-hp"></div>
        <div class="c1-pg-btn" :class="{ disabled: f1Pagination.currentPage >= f1Pagination.lastPage }" @click="f1GoPage(f1Pagination.currentPage + 1)">
          <svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>
        </div>
        <div class="c1-pg-btn" :class="{ disabled: f1Pagination.currentPage >= f1Pagination.lastPage }" @click="f1GoPage(f1Pagination.lastPage)">
          <svg viewBox="0 0 24 24"><polyline points="13 17 18 12 13 7"/><polyline points="6 17 11 12 6 7"/></svg>
        </div>
      </div>

    </div>

    <!-- Loading -->
    <div v-else class="c1-loading">
      <div class="c1-spinner"></div>
      <div style="font-size:11px;color:#A09E98;margin-top:8px">Loading...</div>
    </div>

    <!-- ═══ RIGHT DRAWER — Transaction Detail ═══ -->
    <transition name="c1-drawer">
      <div v-if="f1DetailTx" class="c1-drawer-wrap" @click.self="f1DetailTx = null">
        <div class="c1-drawer">
          <div class="c1-drawer-head">
            <div class="f1-drawer-icon" :style="{ background: f1CatColor(f1DetailTx.category_id) }">
              <svg viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
            </div>
            <div class="c1-drawer-title">
              <div class="c1-drawer-name">{{ f1DetailTx.description || f1DetailTx.category_name || 'Transaction' }}</div>
              <div class="c1-drawer-sub">#{{ f1DetailTx.id }} · {{ f1FmtDate(f1DetailTx.created_at) }}</div>
            </div>
            <button class="c1-drawer-close" @click="f1DetailTx = null">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="c1-drawer-body">

            <!-- Transaction Info -->
            <div class="c1-exp-sec">
              <div class="c1-exp-lbl">Transaction Info</div>
              <div class="c1-exp-grid">
                <div class="c1-exp-field c1-exp-full">
                  <div class="c1-exp-fl">Amount</div>
                  <div class="c1-exp-fv f1-td-big" :class="f1DetailTx.type === 'income' ? 'income' : 'expense'">
                    {{ f1DetailTx.type === 'income' ? '+' : '-' }}{{ f1FormatAmt(f1DetailTx.amount) }}
                  </div>
                </div>
                <div class="c1-exp-field">
                  <div class="c1-exp-fl">Category</div>
                  <div class="c1-exp-fv">
                    <span class="f1-cat-chip" :style="{ background: f1CatBg(f1DetailTx.category_id), color: f1CatColor(f1DetailTx.category_id) }">{{ f1DetailTx.category_name || '—' }}</span>
                  </div>
                </div>
                <div class="c1-exp-field">
                  <div class="c1-exp-fl">Status</div>
                  <div class="c1-exp-fv">
                    <span class="f1-status-chip" :class="'f1-status-' + (f1DetailTx.status || 'pending')">{{ f1DetailTx.status || 'pending' }}</span>
                  </div>
                </div>
                <div v-if="f1DetailTx.payment_method" class="c1-exp-field">
                  <div class="c1-exp-fl">Payment</div>
                  <div class="c1-exp-fv">{{ f1DetailTx.payment_method }}</div>
                </div>
                <div v-if="f1DetailTx.recurring" class="c1-exp-field">
                  <div class="c1-exp-fl">Recurring</div>
                  <div class="c1-exp-fv">{{ f1DetailTx.recurring }}</div>
                </div>
                <div v-if="f1DetailTx.project_name" class="c1-exp-field">
                  <div class="c1-exp-fl">Project</div>
                  <div class="c1-exp-fv">{{ f1DetailTx.project_name }}</div>
                </div>
                <div v-if="f1DetailTx.user_name" class="c1-exp-field">
                  <div class="c1-exp-fl">Added by</div>
                  <div class="c1-exp-fv">{{ f1DetailTx.user_name }}</div>
                </div>
                <div v-if="f1DetailTx.approved_by" class="c1-exp-field">
                  <div class="c1-exp-fl">Approved by</div>
                  <div class="c1-exp-fv">{{ f1DetailTx.approved_by }}</div>
                </div>
                <div v-if="f1DetailTx.description" class="c1-exp-field c1-exp-full">
                  <div class="c1-exp-fl">Description</div>
                  <div class="c1-exp-fv">{{ f1DetailTx.description }}</div>
                </div>
              </div>
            </div>

            <!-- Receipt image -->
            <div v-if="f1DetailTx.receipt_url" class="c1-exp-sec">
              <div class="c1-exp-lbl">Receipt</div>
              <img :src="f1DetailTx.receipt_url" alt="Receipt" class="f1-receipt-full" @click="f1OpenReceipt(f1DetailTx.receipt_url)" />
            </div>

            <!-- Approval history -->
            <div v-if="f1DetailTx.approval_history && f1DetailTx.approval_history.length" class="c1-exp-sec">
              <div class="c1-exp-lbl">Approval History</div>
              <div v-for="(h, hi) in f1DetailTx.approval_history" :key="hi" class="c1-exp-activity-row">
                <div class="c1-exp-a-badge" :style="f1ApprovalBadgeStyle(h.status)">{{ h.status.substring(0,3).toUpperCase() }}</div>
                <div class="c1-exp-a-body">
                  <span style="font-weight:600">{{ h.by }}</span>
                  <span class="c1-exp-a-meta">{{ h.status }}</span>
                </div>
                <div class="c1-exp-a-date f1-mono">{{ f1FmtDate(h.date) }}</div>
              </div>
            </div>

            <!-- Edit form (admin only) -->
            <div v-if="f1IsAdmin" class="c1-exp-sec">
              <div class="c1-exp-lbl">Edit Transaction</div>
              <div class="f1-edit-form">
                <div class="c1-pkf-group">
                  <label class="c1-pkf-lbl">Amount</label>
                  <input type="number" class="c1-pkf-input" v-model.number="f1EditForm.amount" />
                </div>
                <div class="c1-pkf-group">
                  <label class="c1-pkf-lbl">Category</label>
                  <select class="c1-pkf-select" v-model="f1EditForm.category_id">
                    <option :value="null" disabled>Select category…</option>
                    <option v-for="cat in f1Categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                  </select>
                </div>
                <div class="c1-pkf-group">
                  <label class="c1-pkf-lbl">Status</label>
                  <div class="c1-pkf-opts">
                    <button class="c1-pkf-opt" :class="{ on: f1EditForm.status === 'approved' }" @click="f1EditForm.status = 'approved'">Approved</button>
                    <button class="c1-pkf-opt" :class="{ on: f1EditForm.status === 'pending' }"  @click="f1EditForm.status = 'pending'">Pending</button>
                    <button class="c1-pkf-opt" :class="{ on: f1EditForm.status === 'rejected' }" @click="f1EditForm.status = 'rejected'">Rejected</button>
                  </div>
                </div>
                <div class="c1-pkf-group">
                  <label class="c1-pkf-lbl">Description</label>
                  <textarea class="c1-pkf-input" v-model="f1EditForm.description" rows="2"></textarea>
                </div>
                <div class="c1-pkf-group">
                  <label class="c1-pkf-lbl">Delete transaction</label>
                  <button class="f1-delete-btn" @click="f1DeleteTx(f1DetailTx.id)" :disabled="f1Saving">
                    <svg viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                    Delete
                  </button>
                </div>
                <button class="c1-pkf-submit" @click="f1SaveEdit" :disabled="f1Saving">
                  {{ f1Saving ? 'Saving…' : 'Save changes' }}
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </transition>

    <!-- ═══ LEFT DRAWER — Add Transaction ═══ -->
    <transition name="f1-drawer-left">
      <div v-if="f1AddOpen" class="c1-drawer-wrap f1-drawer-left-wrap" @click.self="f1AddOpen = false">
        <div class="c1-drawer f1-add-drawer">
          <div class="c1-drawer-head">
            <div class="f1-drawer-icon" style="background:#6366f1">
              <svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </div>
            <div class="c1-drawer-title">
              <div class="c1-drawer-name">Add Transaction</div>
              <div class="c1-drawer-sub">New expense or income</div>
            </div>
            <button class="c1-drawer-close" @click="f1AddOpen = false">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="c1-drawer-body">
            <div class="f1-edit-form">

              <!-- Type toggle -->
              <div class="c1-pkf-group">
                <label class="c1-pkf-lbl">Type</label>
                <div class="c1-pkf-opts">
                  <button class="c1-pkf-opt f1-type-expense" :class="{ on: f1AddForm.type === 'expense' }" @click="f1AddForm.type = 'expense'">Expense</button>
                  <button class="c1-pkf-opt f1-type-income"  :class="{ on: f1AddForm.type === 'income' }"  @click="f1AddForm.type = 'income'">Income</button>
                </div>
              </div>

              <div class="c1-pkf-group">
                <label class="c1-pkf-lbl">Amount <span style="color:#ef4444">*</span></label>
                <input type="number" class="c1-pkf-input" v-model.number="f1AddForm.amount" placeholder="0.00" min="0" step="0.01" />
              </div>

              <div class="c1-pkf-group">
                <label class="c1-pkf-lbl">Category <span style="color:#ef4444">*</span></label>
                <select class="c1-pkf-select" v-model="f1AddForm.category_id">
                  <option :value="null" disabled>Select category…</option>
                  <option v-for="cat in f1Categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                </select>
              </div>

              <div class="c1-pkf-group">
                <label class="c1-pkf-lbl">Date <span style="color:#ef4444">*</span></label>
                <input type="date" class="c1-pkf-input" v-model="f1AddForm.date" />
              </div>

              <!-- Currency -->
              <div class="c1-pkf-group">
                <label class="c1-pkf-lbl">Currency</label>
                <div class="c1-pkf-opts">
                  <button v-for="cur in f1CurrencyOptions" :key="cur" class="c1-pkf-opt" :class="{ on: f1AddForm.currency === cur }" @click="f1AddForm.currency = cur">{{ cur }}</button>
                </div>
              </div>

              <!-- Purchase Type tags -->
              <div class="c1-pkf-group">
                <label class="c1-pkf-lbl">Purchase Type</label>
                <div class="f1-tag-row">
                  <div
                    v-for="tag in f1PurchaseTypeTags"
                    :key="tag"
                    class="f1-tag-btn"
                    :class="{ on: f1AddForm.purchase_type === tag }"
                    @click="f1AddForm.purchase_type = (f1AddForm.purchase_type === tag ? '' : tag)"
                  >{{ tag }}</div>
                </div>
              </div>

              <!-- Reason tags -->
              <div class="c1-pkf-group">
                <label class="c1-pkf-lbl">Reason</label>
                <div class="f1-tag-row">
                  <div
                    v-for="tag in f1ReasonTags"
                    :key="tag"
                    class="f1-tag-btn f1-tag-reason"
                    :class="{ on: f1AddForm.reason === tag }"
                    @click="f1AddForm.reason = (f1AddForm.reason === tag ? '' : tag)"
                  >{{ tag }}</div>
                </div>
              </div>

              <div class="c1-pkf-group">
                <label class="c1-pkf-lbl">Description</label>
                <textarea class="c1-pkf-input" v-model="f1AddForm.description" rows="2" placeholder="What was this for?"></textarea>
              </div>

              <div class="c1-pkf-group">
                <label class="c1-pkf-lbl">Who paid</label>
                <select class="c1-pkf-select" v-model="f1AddForm.user_id">
                  <option :value="null">Me ({{ f1AuthUser.first_name }})</option>
                  <option v-for="u in f1UserList" :key="u.id" :value="u.id">{{ u.first_name }} {{ u.last_name }}</option>
                </select>
              </div>

              <div class="c1-pkf-group">
                <label class="c1-pkf-lbl">Payment method</label>
                <div class="c1-pkf-opts" style="flex-wrap:wrap">
                  <button v-for="m in f1PaymentMethods" :key="m" class="c1-pkf-opt" :class="{ on: f1AddForm.payment_method === m }" @click="f1AddForm.payment_method = m">{{ m }}</button>
                </div>
              </div>

              <!-- Recurring -->
              <div class="c1-pkf-group">
                <label class="c1-pkf-lbl">Recurring</label>
                <div class="c1-pkf-opts">
                  <button v-for="r in f1RecurringOptions" :key="r" class="c1-pkf-opt" :class="{ on: f1AddForm.recurring === r }" @click="f1AddForm.recurring = r">{{ r }}</button>
                </div>
              </div>

              <!-- Receipt upload -->
              <div class="c1-pkf-group">
                <label class="c1-pkf-lbl">Attach receipt</label>
                <label class="f1-receipt-upload">
                  <svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  <span>{{ f1AddForm.receiptName || 'Choose file…' }}</span>
                  <input type="file" accept="image/*,.pdf" @change="f1HandleReceiptFile" style="display:none" />
                </label>
                <div v-if="f1AddForm.receiptPreview" class="f1-receipt-preview-wrap">
                  <img :src="f1AddForm.receiptPreview" class="f1-receipt-preview" alt="Receipt preview" />
                </div>
              </div>

              <div v-if="f1AddError" class="f1-add-error">{{ f1AddError }}</div>

              <button
                class="c1-pkf-submit"
                @click="f1SubmitAdd"
                :disabled="f1Saving || !f1AddForm.amount || !f1AddForm.category_id || !f1AddForm.date"
              >
                {{ f1Saving ? 'Saving…' : 'Add Transaction' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import VikiFace from "../../clients1.feature/VikiFace.vue";
import C1ModCalendar from '../../../components/C1ModCalendar.vue';

const API = "/api";

// Budgets per category id (can be overridden by backend later)
const CAT_BUDGETS = { 1: 5000, 2: 3000, 3: 2000, 4: 1500, 5: 8000, 6: 1200, 7: 4000, 8: 600, 9: 2500, 10: 3500 };
const DAILY_BUDGET = 1500; // daily spend limit for Viki

// Plan pricing from master brain
const PLAN_DEFS = [
  { name: "Standard",              price: 7  },
  { name: "Pro Club (no WA)",      price: 15 },
  { name: "Pro Club (WhatsApp)",   price: 15 },
  { name: "Unlimited",             price: 26 },
  { name: "President",             price: 100 }
];

export default {
  name: "Finance1Container",

  components: { VikiFace, C1ModCalendar },

  data() {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm   = String(today.getMonth() + 1).padStart(2, "0");
    const dd   = String(today.getDate()).padStart(2, "0");

    return {
      c1Status: 'available',
      c1StatusSince: 0,
      c1StatusElapsed: '00:00',
      c1StatusMenuOpen: false,
      _c1StatusInterval: null,
      c1HamMenuOpen: false,
      c1ShiftMenuOpen: false,
      c1TopTab: null,
      c1ShiftElapsed: '00:00',
      c1ShiftStartTime: 0,
      c1BathroomCount: 0,
      c1CoffeeCount: 0,
      c1WeekLog: [],
      // Password Gate
      f1Unlocked: false,
      f1GatePassword: '',
      f1GateError: null,
      f1GateLoading: false,

      // Panel
      c1NavMode: 'panels',
      c1VikiMood: 'neutral',
      c1VikiSpeaking: false,
      f1UserOnline: true,
      c1SlideOpen: null,
      c1PanelActiveContent: null,

      // Toast
      f1Toast: null,
      f1ToastTimer: null,

      // Currency & Tags
      f1CurrencyOptions: ['USD', 'EUR', 'GBP', 'ILS'],
      f1PurchaseTypeTags: ['Media Buy', 'Software', 'Salary', 'Office', 'Travel', 'Marketing', 'Hardware', 'Services', 'Legal', 'Other'],
      f1ReasonTags: ['Campaign Cost', 'Client Acquisition', 'Operations', 'Infrastructure', 'Commission', 'Bonus', 'Refund', 'Tax', 'Miscellaneous'],

      // Data
      f1Transactions: [],
      f1Categories:   [],
      f1UserList:     [],
      f1Loading:      false,
      f1Saving:       false,
      f1AddError:     null,

      // Graph / calendar raw data from API
      f1GraphData:   [],
      f1CalApiData:  {},

      // Campaign / profitability data (mock enriched from transactions)
      f1CampaignData: [],

      // MRR plan counts (mock — replace with real API when available)
      f1MRRCounts: { "Standard": 42, "Pro Club (no WA)": 18, "Pro Club (WhatsApp)": 31, "Unlimited": 9, "President": 3 },

      // Pagination
      f1Pagination: { currentPage: 1, lastPage: 1, total: 0, from: 0, to: 0 },

      // Filters
      f1DateFilter: "month",
      f1Filters: {
        date_from:       null,
        date_to:         null,
        category_ids:    [],
        amount_min:      null,
        amount_max:      null,
        user_id:         null,
        payment_method:  null,
        status:          null,
        search:          "",
        date:            null
      },

      // Calendar
      f1Cal: { year: today.getFullYear(), month: today.getMonth() },
      f1CalMonthNames: ["January","February","March","April","May","June","July","August","September","October","November","December"],
      f1CalDayData: {},

      // Static lists
      f1PaymentMethods:  ["Cash","Card","Bank Transfer","PayPal","Crypto","Other"],
      f1RecurringOptions: ["One-time","Daily","Weekly","Monthly"],

      // Expanded row
      f1ExpandedId: null,

      // Right drawer
      f1DetailTx: null,
      f1EditForm: { amount: 0, category_id: null, status: "pending", description: "" },

      // Left drawer
      f1AddOpen: false,
      f1AddForm: {
        type: "expense", amount: null, category_id: null,
        date: `${yyyy}-${mm}-${dd}`, description: "",
        payment_method: null, user_id: null,
        recurring: "One-time",
        currency: "USD", purchase_type: "", reason: "",
        receiptName: "", receiptFile: null, receiptPreview: null
      },

      // Debounce
      f1SearchTimer: null,

      // Avatar colors
      f1AvatarColors: ["#7C5CFC","#C75450","#D4A017","#2D9D5E","#E8891C","#1144bc","#E05A3A","#6366f1"],

      // CSV export
      f1ExportLoading: false,

      // Team scope
      f1TeamScope: null,    // null = Company-wide | 1 = Team 1 | 2 = Team 2
      f1TeamsInfo: []       // from /api/admin/teams
    };
  },

  watch: {
    c1SlideOpen(val) {
      if (val && val !== 'nav' && val !== 'menu') {
        this.c1PanelActiveContent = val;
      }
    }
  },

  computed: {
    c1PanelIsOpen() {
      return !!this.c1SlideOpen && this.c1SlideOpen !== 'menu';
    },
    c1PanelAtContent() {
      return this.c1PanelIsOpen && this.c1SlideOpen !== 'nav';
    },
    c1ActivePanelLabel() {
      if (!this.c1PanelIsOpen || this.c1SlideOpen === 'nav') return 'Panels';
      const labels = { filters: 'Filters', charts: 'Analytics', calendar: 'Calendar', viki: 'Viki AI' };
      return labels[this.c1PanelActiveContent] || 'Panels';
    },

    f1AuthUser() {
      return (this.$store.state.user && this.$store.state.user.auth && this.$store.state.user.auth.user) || {};
    },
    f1UserInitials() {
      const u = this.f1AuthUser;
      return ((u.first_name || "")[0] || "").toUpperCase() + ((u.last_name || "")[0] || "").toUpperCase() || "?";
    },
    f1RoleLabel() {
      const map = { 1:"Admin",2:"Team Leader",3:"Manager",4:"Senior Agent",5:"Agent",6:"Junior Agent",10:"Finance",12:"Super Admin" };
      return map[this.f1AuthUser.role] || "User";
    },
    f1IsAdmin() {
      const r = this.f1AuthUser.role;
      return r === 1 || r === 12 || r === 10;
    },
    f1Token() {
      return (this.$store.state.user && this.$store.state.user.auth && this.$store.state.user.auth.token) || localStorage.getItem("user-token") || "";
    },

    // ── Active filter count ──
    f1ActiveFilterCount() {
      let n = 0;
      if (this.f1DateFilter !== "month") n++;
      if (this.f1Filters.category_ids.length) n++;
      if (this.f1Filters.amount_min) n++;
      if (this.f1Filters.amount_max) n++;
      if (this.f1Filters.user_id) n++;
      if (this.f1Filters.payment_method) n++;
      if (this.f1Filters.status) n++;
      if (this.f1Filters.search) n++;
      if (this.f1Filters.date) n++;
      return n;
    },

    // ── Summary numbers ──
    f1SummaryTotal() {
      return this.f1Transactions.reduce((s, tx) => s + parseFloat(tx.amount || 0), 0);
    },
    f1SummaryIncome() {
      return this.f1Transactions.filter(tx => tx.type === "income").reduce((s, tx) => s + parseFloat(tx.amount || 0), 0);
    },
    f1SummaryExpense() {
      return this.f1Transactions.filter(tx => tx.type !== "income").reduce((s, tx) => s + parseFloat(tx.amount || 0), 0);
    },
    f1NetBalance() {
      return this.f1SummaryIncome - this.f1SummaryExpense;
    },
    f1AvgPerDay() {
      if (!this.f1Transactions.length) return 0;
      const dates = new Set(this.f1Transactions.map(tx => (tx.created_at || "").substring(0, 10)));
      return this.f1SummaryTotal / (dates.size || 1);
    },
    f1TopCatId() {
      const map = {};
      this.f1Transactions.forEach(tx => {
        const cid = tx.category_id || 0;
        map[cid] = (map[cid] || 0) + parseFloat(tx.amount || 0);
      });
      let topId = null, topAmt = 0;
      Object.keys(map).forEach(k => { if (map[k] > topAmt) { topAmt = map[k]; topId = parseInt(k); } });
      return topId;
    },

    // ── Donut chart ──
    f1DonutSegments() {
      const map = {};
      this.f1Transactions.forEach(tx => {
        const cid = tx.category_id || 0;
        if (!map[cid]) map[cid] = { id: cid, name: tx.category_name || "Other", amount: 0 };
        map[cid].amount += parseFloat(tx.amount || 0);
      });
      const total = Object.values(map).reduce((s, v) => s + v.amount, 0) || 1;
      const sorted = Object.values(map).sort((a, b) => b.amount - a.amount).slice(0, 7);
      let offset = 0;
      return sorted.map(seg => {
        const pct = Math.round((seg.amount / total) * 100);
        const dash = (seg.amount / total) * 88;
        const result = { ...seg, pct, dash, offset, color: this.f1CatColor(seg.id) };
        offset += dash;
        return result;
      });
    },

    // ── Daily bars (last 30 days) ──
    f1DailyBars() {
      const map = {};
      this.f1Transactions.forEach(tx => {
        const d = (tx.created_at || "").substring(0, 10);
        if (d) map[d] = (map[d] || 0) + parseFloat(tx.amount || 0);
      });
      // also merge in API graph data
      (this.f1GraphData || []).forEach(item => {
        if (item.date && item.total) map[item.date] = (map[item.date] || 0) + parseFloat(item.total);
      });
      const days = Object.keys(map).sort().slice(-30);
      if (!days.length) return [];
      const maxAmt = Math.max(...days.map(d => map[d]), 1);
      return days.map(d => ({
        label: d.substring(5),
        amount: map[d],
        pct: Math.round((map[d] / maxAmt) * 100)
      }));
    },

    // ── Line chart SVG paths ──
    f1LinePoints() {
      const bars = this.f1DailyBars;
      if (bars.length < 2) return [];
      const maxAmt = Math.max(...bars.map(b => b.amount), 1);
      const W = 300, H = 80, PAD = 4;
      return bars.map((b, i) => ({
        x: PAD + (i / (bars.length - 1)) * (W - PAD * 2),
        y: H - PAD - ((b.amount / maxAmt) * (H - PAD * 2))
      }));
    },
    f1LinePath() {
      const pts = this.f1LinePoints;
      if (!pts.length) return "";
      return pts.map((p, i) => (i === 0 ? `M${p.x},${p.y}` : `L${p.x},${p.y}`)).join(" ");
    },
    f1LineArea() {
      const pts = this.f1LinePoints;
      if (!pts.length) return "";
      const H = 80, PAD = 4;
      const line = pts.map((p, i) => (i === 0 ? `M${p.x},${p.y}` : `L${p.x},${p.y}`)).join(" ");
      return `${line} L${pts[pts.length - 1].x},${H - PAD} L${pts[0].x},${H - PAD} Z`;
    },

    // ── Budget vs actual ──
    f1BudgetRows() {
      return this.f1Categories.slice(0, 8).map(cat => {
        const actual = this.f1Transactions
          .filter(tx => tx.category_id === cat.id && tx.type !== "income")
          .reduce((s, tx) => s + parseFloat(tx.amount || 0), 0);
        const budget = CAT_BUDGETS[cat.id] || 2000;
        const pct = Math.round((actual / budget) * 100);
        return { name: cat.name, catId: cat.id, actual, budget, pct, over: pct > 100 };
      }).filter(b => b.actual > 0);
    },

    // ── Per-sector profitability ──
    f1ProfitRows() {
      if (!this.f1CampaignData.length) return [];
      return this.f1CampaignData.map(c => {
        const cpl_total = (c.leads || 0) * (c.cpl || 0);
        const call_cost = (c.calls || 0) * 0.3 * (c.avg_call_min || 3);
        const finance_cost = (c.finance_expenses || 0);
        const cost = cpl_total + call_cost + finance_cost;
        const revenue = c.deposits_amount || 0;
        const profit = revenue - cost;
        const roi = cost > 0 ? Math.round((profit / cost) * 100) : 0;
        return {
          name: c.name || c.campaign_name || "Campaign",
          cost, revenue, profit, roi,
          cpl: c.cpl || 0,
          calls: c.calls || 0,
          call_cost
        };
      }).sort((a, b) => b.profit - a.profit);
    },

    // ── MRR ──
    f1MRRPlans() {
      return PLAN_DEFS.map(p => ({
        ...p,
        count: this.f1MRRCounts[p.name] || 0
      }));
    },
    f1TotalMRR() {
      return this.f1MRRPlans.reduce((s, p) => s + p.price * p.count, 0);
    },

    // ── Calendar grid ──
    f1CalGrid() {
      const { year, month } = this.f1Cal;
      const first = new Date(year, month, 1);
      const startDow = (first.getDay() + 6) % 7;
      const todayStr = new Date().toISOString().substring(0, 10);
      const cursor = new Date(first);
      cursor.setDate(cursor.getDate() - startDow);
      const cells = [];
      for (let i = 0; i < 42; i++) {
        const dateStr = cursor.toISOString().substring(0, 10);
        const inMonth = cursor.getMonth() === month;
        const dayData = this.f1CalDayData[dateStr] || {};
        cells.push({
          date: dateStr,
          day: cursor.getDate(),
          inMonth,
          today: dateStr === todayStr,
          count: dayData.count || 0,
          total: dayData.total || 0,
          overBudget: dayData.total > DAILY_BUDGET
        });
        cursor.setDate(cursor.getDate() + 1);
        if (i >= 27 && cursor.getMonth() !== month) break;
      }
      return cells;
    },
    f1CalDayTotal() {
      const d = this.f1Filters.date;
      if (!d) return "—";
      return this.f1FormatAmt(
        this.f1Transactions.filter(tx => (tx.created_at || "").substring(0, 10) === d)
          .reduce((s, tx) => s + parseFloat(tx.amount || 0), 0)
      );
    },
    f1CalDayCount() {
      const d = this.f1Filters.date;
      if (!d) return 0;
      return this.f1Transactions.filter(tx => (tx.created_at || "").substring(0, 10) === d).length;
    },

    // ── Daily budget ──
    f1DailyBudgetLimit() { return DAILY_BUDGET; },
    f1TodaySpend() {
      const today = new Date().toISOString().substring(0, 10);
      return this.f1Transactions
        .filter(tx => (tx.created_at || "").substring(0, 10) === today && tx.type !== "income")
        .reduce((s, tx) => s + parseFloat(tx.amount || 0), 0);
    },
    f1TodayBudgetPct() {
      return Math.round((this.f1TodaySpend / DAILY_BUDGET) * 100);
    },
    f1TodayTopTx() {
      const today = new Date().toISOString().substring(0, 10);
      return this.f1Transactions
        .filter(tx => (tx.created_at || "").substring(0, 10) === today && tx.type !== "income")
        .sort((a, b) => parseFloat(b.amount) - parseFloat(a.amount))
        .slice(0, 5);
    },

    // ── Viki anomaly alerts ──
    f1VikiAlerts() {
      const alerts = [];
      // Build 30-day avg per category
      const today = new Date().toISOString().substring(0, 10);
      const catMap30 = {};
      const catMapToday = {};
      this.f1Transactions.forEach(tx => {
        if (tx.type === "income") return;
        const cid = tx.category_id || 0;
        const cname = tx.category_name || "Unknown";
        const d = (tx.created_at || "").substring(0, 10);
        if (!catMap30[cid]) catMap30[cid] = { name: cname, days: new Set(), total: 0 };
        catMap30[cid].total += parseFloat(tx.amount || 0);
        catMap30[cid].days.add(d);
        if (d === today) {
          catMapToday[cid] = (catMapToday[cid] || 0) + parseFloat(tx.amount || 0);
        }
      });
      Object.keys(catMapToday).forEach(cid => {
        const data = catMap30[cid];
        if (!data || data.days.size < 2) return;
        const avgPerDay = data.total / data.days.size;
        const todayAmt = catMapToday[cid];
        const pct = avgPerDay > 0 ? Math.round(((todayAmt - avgPerDay) / avgPerDay) * 100) : 0;
        if (pct >= 150) {
          alerts.push({
            id: "anom-" + cid,
            level: "warn",
            msg: `Unusual spend in ${data.name} today: +${pct}% above average`,
            meta: `Today: ${this.f1FormatAmt(todayAmt)} · Avg/day: ${this.f1FormatAmt(avgPerDay)}`
          });
        } else if (pct >= 80) {
          alerts.push({
            id: "anom-info-" + cid,
            level: "info",
            msg: `Elevated spend in ${data.name}: +${pct}% above average`,
            meta: `Today: ${this.f1FormatAmt(todayAmt)} · Avg/day: ${this.f1FormatAmt(avgPerDay)}`
          });
        }
      });
      // Budget breach alerts
      this.f1BudgetRows.forEach(b => {
        if (b.pct >= 100) {
          alerts.push({
            id: "budget-" + b.catId,
            level: "warn",
            msg: `${b.name} is over budget (${b.pct}%)`,
            meta: `Spent ${this.f1FormatAmt(b.actual)} of ${this.f1FormatAmt(b.budget)} budget`
          });
        }
      });
      return alerts;
    },

    // ── Viki: this week by category ──
    f1WeekCatBreakdown() {
      const now = new Date();
      const dow = (now.getDay() + 6) % 7;
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - dow);
      const ws = weekStart.toISOString().substring(0, 10);
      const map = {};
      this.f1Transactions.forEach(tx => {
        if (tx.type === "income") return;
        const d = (tx.created_at || "").substring(0, 10);
        if (d < ws) return;
        const cid = tx.category_id || 0;
        const cname = tx.category_name || "Other";
        if (!map[cid]) map[cid] = { name: cname, amount: 0, color: this.f1CatColor(cid) };
        map[cid].amount += parseFloat(tx.amount || 0);
      });
      const arr = Object.values(map).sort((a, b) => b.amount - a.amount).slice(0, 6);
      const maxAmt = Math.max(...arr.map(i => i.amount), 1);
      return arr.map(i => ({ ...i, pct: Math.round((i.amount / maxAmt) * 100) }));
    },
    c1CurrentPageLabel() {
      const map = {
        '/clients1':'Clients', '/tasks1':'Tasks', '/stats1':'Statistics',
        '/chat1':'Chat', '/communications1':'Communications', '/affiliation1':'Affiliation',
        '/finance1':'Finance', '/shifts1':'Shifts', '/games1':'Games',
        '/users1':'Users', '/brokers1':'Brokers', '/security1':'Security',
        '/quiz1':'Quiz', '/instruction1':'Instructions', '/settings1':'Settings', '/profile1':'My Profile',
      };
      return map[this.$route.path] || 'Menu';
    },
    c1UserInitials() {
      const u = this.$store.state.user && this.$store.state.user.auth && this.$store.state.user.auth.user;
      if (!u) return "?";
      return ((u.first_name || "")[0] || "").toUpperCase() + ((u.last_name || "")[0] || "").toUpperCase();
    },
    c1UserFullName() {
      const u = this.$store.state.user && this.$store.state.user.auth && this.$store.state.user.auth.user;
      if (!u) return "User";
      return ((u.first_name || "") + " " + (u.last_name || "")).trim() || "User";
    },
    c1RoleLabel() {
      const u = this.$store.state.user && this.$store.state.user.auth && this.$store.state.user.auth.user;
      const map = { 1:"Admin",2:"Team Leader",3:"Manager",4:"Senior Agent",5:"Agent",6:"Junior Agent",10:"Finance",12:"Super Admin" };
      return map[u && u.role] || "User";
    },
    c1TzUK() { return this.c1TzTime("Europe/London"); },
    c1TzIL() { return this.c1TzTime("Asia/Jerusalem"); },
    c1TzCB() { return this.c1TzTime("America/Cayman"); },
  },

  created() {
    const _keepNav = sessionStorage.getItem('c1keepNav'); if (_keepNav) sessionStorage.removeItem('c1keepNav');
    const _openPanel = sessionStorage.getItem('c1openPanel'); if (_openPanel) sessionStorage.removeItem('c1openPanel');
    if (_openPanel) { this.c1SlideOpen = 'filters'; }
    else if (_keepNav) { this.c1SlideOpen = 'nav'; this.c1NavMode = 'panels'; }
    this.f1ApplyDateFilter("month");
    this.f1FetchCategories();
    this.f1FetchUsers();
    this.f1FetchCampaignData();
    this._f1FetchTeams();
  },

  beforeDestroy() {
    if (this._c1StatusInterval) clearInterval(this._c1StatusInterval);
  },

  mounted() {
    const savedStatus = localStorage.getItem('c1_status');
    const savedSince  = localStorage.getItem('c1_status_since');
    const today = new Date().toISOString().slice(0, 10);
    const savedShiftDay = localStorage.getItem('c1_shift_day');
    if (savedShiftDay !== today) {
      this.c1ShiftStartTime = Date.now();
      this.c1BathroomCount = 0;
      this.c1CoffeeCount = 0;
      localStorage.setItem('c1_status', 'shift');
      localStorage.setItem('c1_status_since', String(Date.now()));
      localStorage.setItem('c1_shift_start', String(Date.now()));
      localStorage.setItem('c1_shift_day', today);
      localStorage.setItem('c1_bathroom_count', '0');
      localStorage.setItem('c1_coffee_count', '0');
    } else {
      if (savedStatus) this.c1Status = savedStatus;
      this.c1ShiftStartTime = parseInt(localStorage.getItem('c1_shift_start') || String(Date.now()), 10);
      this.c1BathroomCount = parseInt(localStorage.getItem('c1_bathroom_count') || '0', 10);
      this.c1CoffeeCount   = parseInt(localStorage.getItem('c1_coffee_count') || '0', 10);
    }
    this.c1StatusSince = savedSince ? parseInt(savedSince, 10) : Date.now();
    this._c1StatusInterval = setInterval(this.c1TickStatus, 1000);
    document.documentElement.style.setProperty('--gnm-sw', '0px');
  },

  methods: {
    c1SetStatus(s) {
      if (s === 'bathroom') this.c1BathroomCount++;
      if (s === 'coffee')   this.c1CoffeeCount++;
      this.c1Status = s;
      this.c1StatusSince = Date.now();
      this.c1StatusElapsed = '00:00';
      this.c1ShiftMenuOpen = false;
      localStorage.setItem('c1_status', s);
      localStorage.setItem('c1_status_since', String(this.c1StatusSince));
      if (s === 'bathroom') localStorage.setItem('c1_bathroom_count', String(this.c1BathroomCount));
      if (s === 'coffee')   localStorage.setItem('c1_coffee_count',   String(this.c1CoffeeCount));
      if (s === 'end') this.c1SaveTodayToLog();
    },
    c1TickStatus() {
      const fmtSec = (sec) => {
        const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s2 = sec % 60;
        return h > 0
          ? String(h).padStart(2,'0') + ':' + String(m).padStart(2,'0') + ':' + String(s2).padStart(2,'0')
          : String(m).padStart(2,'0') + ':' + String(s2).padStart(2,'0');
      };
      this.c1StatusElapsed = fmtSec(Math.floor((Date.now() - this.c1StatusSince) / 1000));
      this.c1ShiftElapsed  = fmtSec(Math.floor((Date.now() - this.c1ShiftStartTime) / 1000));
    },
    c1SaveTodayToLog() {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const workedMin = Math.floor((Date.now() - this.c1ShiftStartTime) / 60000);
        const log = JSON.parse(localStorage.getItem('c1_week_log') || '[]').filter(r => r.date !== today);
        log.unshift({ date: today, workedMin: Math.max(0, workedMin), bathroom: this.c1BathroomCount, coffee: this.c1CoffeeCount });
        localStorage.setItem('c1_week_log', JSON.stringify(log.slice(0, 14)));
        this.c1WeekLog = log.slice(0, 14);
      } catch(e) {}
    },
    c1TzTime(tz) {
      try {
        return new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: tz });
      } catch(e) { return "--:--"; }
    },
    c1Logout() {
      this.$store.dispatch('user/logOut');
    },
    // ── Panel control ──
    c1OpenSlide(name) {
      this.c1SlideOpen = this.c1SlideOpen === name ? null : name;
    },
    closeAllSlides() { this.c1SlideOpen = null; },
    c1PanelBtnClick() {
      this.c1SlideOpen = this.c1PanelIsOpen ? null : 'nav';
    },
    c1SelectPanel(panel) {
      this.c1SlideOpen = panel;
    },
    c1FilterPanelClick(e) {
      // Panel stays open after selecting a filter option — user uses back (←) or X to navigate/close
    },

    // ── Password gate ──
    async f1VerifyPassword() {
      if (!this.f1GatePassword) return;
      this.f1GateLoading = true;
      this.f1GateError = null;
      try {
        const token = this.f1Token;
        const res = await fetch(`${API}/auth/verify-password`, {
          method: 'POST',
          headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
          body: JSON.stringify({ password: this.f1GatePassword })
        });
        if (res.ok) {
          const json = await res.json();
          if (json.success || json.verified || json.status === 'ok') {
            this.f1Unlocked = true;
            this.f1GatePassword = '';
          } else {
            this.f1GateError = 'Incorrect password. Try again.';
          }
        } else if (res.status === 401 || res.status === 403) {
          this.f1GateError = 'Incorrect password. Try again.';
        } else {
          // If endpoint doesn't exist yet, allow access
          this.f1Unlocked = true;
          this.f1GatePassword = '';
        }
      } catch (e) {
        // Network error or endpoint not available — grant access
        this.f1Unlocked = true;
        this.f1GatePassword = '';
      } finally {
        this.f1GateLoading = false;
      }
    },

    // ── Auth header ──
    f1AuthHeaders() {
      return { Authorization: "Bearer " + this.f1Token, "Content-Type": "application/json" };
    },

    // ── Toast ──
    f1ShowToast(msg, color = "#4ade80") {
      clearTimeout(this.f1ToastTimer);
      this.f1Toast = { msg, color };
      this.f1ToastTimer = setTimeout(() => { this.f1Toast = null; }, 3000);
    },

    // ── Fetch transactions ──
    async f1Fetch() {
      this.f1Loading = true;
      try {
        const params = new URLSearchParams();
        params.set("page", this.f1Pagination.currentPage);
        if (this.f1Filters.date) {
          params.set("date_from", this.f1Filters.date);
          params.set("date_to",   this.f1Filters.date);
        } else {
          if (this.f1Filters.date_from) params.set("date_from", this.f1Filters.date_from);
          if (this.f1Filters.date_to)   params.set("date_to",   this.f1Filters.date_to);
        }
        if (this.f1Filters.category_ids.length === 1) params.set("category_id", this.f1Filters.category_ids[0]);
        if (this.f1Filters.category_ids.length > 1)  params.set("category_ids", this.f1Filters.category_ids.join(","));
        if (this.f1Filters.amount_min)     params.set("amount_min",     this.f1Filters.amount_min);
        if (this.f1Filters.amount_max)     params.set("amount_max",     this.f1Filters.amount_max);
        if (this.f1Filters.user_id)        params.set("user_id",        this.f1Filters.user_id);
        if (this.f1Filters.payment_method) params.set("payment_method", this.f1Filters.payment_method);
        if (this.f1Filters.status)         params.set("status",         this.f1Filters.status);
        if (this.f1Filters.search)         params.set("search",         this.f1Filters.search);

        const res = await fetch(`${API}/finance/list?${params.toString()}`, { headers: this.f1AuthHeaders() });
        if (!res.ok) throw new Error("fetch failed");
        const json = await res.json();

        const data = json.data || json;
        this.f1Transactions = Array.isArray(data) ? data : (data.data || []);

        if (json.meta || json.pagination) {
          const pg = json.meta || json.pagination;
          this.f1Pagination = {
            currentPage: pg.current_page || 1,
            lastPage:    pg.last_page    || 1,
            total:       pg.total        || this.f1Transactions.length,
            from:        pg.from         || 1,
            to:          pg.to           || this.f1Transactions.length
          };
        } else {
          this.f1Pagination.total = this.f1Transactions.length;
          this.f1Pagination.from  = 1;
          this.f1Pagination.to    = this.f1Transactions.length;
        }

        this.f1BuildCalDayData();
        this.f1FetchGraphData();
      } catch (e) {
        console.error("Finance1: fetch error", e);
        this.f1Transactions = [];
      } finally {
        this.f1Loading = false;
      }
    },

    // ── Fetch categories ──
    async f1FetchCategories() {
      try {
        const res = await fetch(`${API}/finance/categories`, { headers: this.f1AuthHeaders() });
        if (!res.ok) return;
        const json = await res.json();
        this.f1Categories = Array.isArray(json) ? json : (json.data || []);
      } catch (e) { console.error("Finance1: categories error", e); }
    },

    // ── Fetch users ──
    async f1FetchUsers() {
      try {
        const res = await fetch(`${API}/users/table`, { headers: this.f1AuthHeaders() });
        if (!res.ok) return;
        const json = await res.json();
        this.f1UserList = Array.isArray(json) ? json : (json.data || []);
      } catch (e) { /* non-critical */ }
    },

    // ── Fetch graph data ──
    async f1FetchGraphData() {
      try {
        // Backend exposes: finance/stats/graph/methods, deducted, category, projects
        const res = await fetch(`${API}/finance/stats/graph/category`, { headers: this.f1AuthHeaders() });
        if (!res.ok) return;
        const json = await res.json();
        this.f1GraphData = Array.isArray(json) ? json : (json.data || []);
      } catch (e) { /* non-critical */ }
    },

    // ── Fetch calendar stats ──
    async f1FetchCalStats() {
      try {
        const year  = this.f1Cal.year;
        const month = this.f1Cal.month;
        // Backend route: finance/stats/calendar/{cal} — pass YYYY-MM as cal identifier
        const cal = `${year}-${String(month + 1).padStart(2, "0")}`;
        const res = await fetch(`${API}/finance/stats/calendar/${cal}`, { headers: this.f1AuthHeaders() });
        if (!res.ok) return;
        const json = await res.json();
        const arr = Array.isArray(json) ? json : (json.data || []);
        const map = {};
        arr.forEach(d => { if (d.date) map[d.date] = { count: d.count || 0, total: parseFloat(d.total || 0) }; });
        this.f1CalApiData = map;
      } catch (e) { /* non-critical */ }
    },

    // ── Fetch campaign/profitability data ──
    async f1FetchCampaignData() {
      // Mock data until real endpoint is available
      // Replace with: fetch(`${API}/finance/campaigns`, { headers: this.f1AuthHeaders() })
      this.f1CampaignData = [
        { name: "Facebook Q1",  leads: 120, cpl: 8,  calls: 340, avg_call_min: 4, deposits_amount: 18500, finance_expenses: 800 },
        { name: "Google Ads",   leads: 85,  cpl: 12, calls: 210, avg_call_min: 3, deposits_amount: 9200,  finance_expenses: 400 },
        { name: "TikTok March", leads: 200, cpl: 5,  calls: 480, avg_call_min: 2, deposits_amount: 7800,  finance_expenses: 200 },
        { name: "Email Blast",  leads: 40,  cpl: 2,  calls: 90,  avg_call_min: 5, deposits_amount: 4400,  finance_expenses: 150 }
      ];
    },

    // ── Team bar chart percentage ──
    f1TeamBarPct(teamId) {
      const t1 = this.f1TeamSpend(1) || 0;
      const t2 = this.f1TeamSpend(2) || 0;
      const max = Math.max(t1, t2, 1);
      const val = teamId === 1 ? t1 : t2;
      return Math.round((val / max) * 100);
    },

    // ── Fetch teams ──
    async _f1FetchTeams() {
      try {
        const res = await fetch(`${API}/admin/teams`, { headers: this.f1AuthHeaders() });
        if (!res.ok) throw new Error('no teams endpoint');
        const json = await res.json();
        this.f1TeamsInfo = Array.isArray(json) ? json : (json.data || json.teams || []);
      } catch (e) {
        this.f1TeamsInfo = [
          { id: 1, name: 'Team 1' },
          { id: 2, name: 'Team 2' }
        ];
      }
    },

    // ── Team scope filter ──
    f1SetTeamScope(scope) {
      this.f1TeamScope = scope;
    },

    // ── Team spend helpers ──
    f1TeamSpend(teamId) {
      // Finance records link to users via user_id.
      // We look up which users belong to the given team using f1UserList (has team_id field if backend sends it).
      const teamUserIds = this.f1UserList
        .filter(u => u.team_id === teamId)
        .map(u => u.id);
      if (!teamUserIds.length) return null; // null signals "no user-link data"
      return this.f1Transactions
        .filter(tx => teamUserIds.includes(tx.user_id) && tx.type !== 'income')
        .reduce((s, tx) => s + parseFloat(tx.amount || 0), 0);
    },

    f1HasUserTeamData() {
      // Returns true if any user in f1UserList has a team_id field
      return this.f1UserList.some(u => u.team_id != null);
    },

    f1TeamFilteredTransactions() {
      if (!this.f1TeamScope) return this.f1Transactions;
      if (!this.f1HasUserTeamData()) return this.f1Transactions;
      const teamUserIds = this.f1UserList
        .filter(u => u.team_id === this.f1TeamScope)
        .map(u => u.id);
      return this.f1Transactions.filter(tx => teamUserIds.includes(tx.user_id));
    },

    // ── Calendar month start helper ──
    f1CalMonthStart() {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
    },

    // ── Build calendar dot data from transactions ──
    f1BuildCalDayData() {
      const map = {};
      this.f1Transactions.forEach(tx => {
        const d = (tx.created_at || "").substring(0, 10);
        if (!d) return;
        if (!map[d]) map[d] = { count: 0, total: 0 };
        map[d].count++;
        map[d].total += parseFloat(tx.amount || 0);
      });
      // Merge API calendar data (may have more complete data)
      Object.keys(this.f1CalApiData).forEach(d => {
        if (!map[d]) map[d] = { count: 0, total: 0 };
        if (this.f1CalApiData[d].count > map[d].count) map[d].count = this.f1CalApiData[d].count;
        if (this.f1CalApiData[d].total > map[d].total) map[d].total = this.f1CalApiData[d].total;
      });
      this.f1CalDayData = map;
    },

    // ── Date filter presets ──
    f1ApplyDateFilter(preset) {
      this.f1DateFilter = preset;
      const today = new Date();
      const fmt   = d => d.toISOString().substring(0, 10);
      if (preset === "today") {
        const t = fmt(today);
        this.f1Filters.date_from = t; this.f1Filters.date_to = t;
      } else if (preset === "yesterday") {
        const y = new Date(today); y.setDate(today.getDate() - 1);
        const ys = fmt(y);
        this.f1Filters.date_from = ys; this.f1Filters.date_to = ys;
      } else if (preset === "week") {
        const dow = (today.getDay() + 6) % 7;
        const start = new Date(today); start.setDate(today.getDate() - dow);
        this.f1Filters.date_from = fmt(start); this.f1Filters.date_to = fmt(today);
      } else if (preset === "lastweek") {
        const dow = (today.getDay() + 6) % 7;
        const end = new Date(today); end.setDate(today.getDate() - dow - 1);
        const start = new Date(end); start.setDate(end.getDate() - 6);
        this.f1Filters.date_from = fmt(start); this.f1Filters.date_to = fmt(end);
      } else if (preset === "month") {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        this.f1Filters.date_from = fmt(start); this.f1Filters.date_to = fmt(today);
      } else if (preset === "lastmonth") {
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end   = new Date(today.getFullYear(), today.getMonth(), 0);
        this.f1Filters.date_from = fmt(start); this.f1Filters.date_to = fmt(end);
      } else {
        this.f1Filters.date_from = null; this.f1Filters.date_to = null;
      }
      this.f1Filters.date = null;
      this.f1Pagination.currentPage = 1;
      this.f1Fetch();
    },

    // ── Category multi-select toggle ──
    f1ToggleCategoryFilter(catId) {
      const arr = [...this.f1Filters.category_ids];
      const idx = arr.indexOf(catId);
      if (idx === -1) arr.push(catId); else arr.splice(idx, 1);
      this.f1Filters.category_ids = arr;
      this.f1Pagination.currentPage = 1;
      this.f1Fetch();
    },

    // ── Reset all filters ──
    f1ResetFilters() {
      this.f1Filters = {
        date_from: null, date_to: null, category_ids: [],
        amount_min: null, amount_max: null, user_id: null,
        payment_method: null, status: null, search: "", date: null
      };
      this.f1DateFilter = "month";
      this.f1ApplyDateFilter("month");
    },

    // ── Calendar nav ──
    f1CalPrev() {
      if (this.f1Cal.month === 0) { this.f1Cal.month = 11; this.f1Cal.year--; }
      else this.f1Cal.month--;
      this.f1FetchCalStats();
    },
    f1CalNext() {
      if (this.f1Cal.month === 11) { this.f1Cal.month = 0; this.f1Cal.year++; }
      else this.f1Cal.month++;
      this.f1FetchCalStats();
    },
    f1CalDayClick(cell) {
      if (!cell.inMonth) return;
      if (this.f1Filters.date === cell.date) {
        this.f1Filters.date = null;
      } else {
        this.f1Filters.date = cell.date;
        this.f1DateFilter = "custom";
      }
      this.f1Pagination.currentPage = 1;
      this.f1Fetch();
    },

    // ── Pagination ──
    f1GoPage(n) {
      if (n < 1 || n > this.f1Pagination.lastPage) return;
      this.f1Pagination.currentPage = n;
      this.f1Fetch();
    },

    // ── Accordion ──
    f1ToggleExpand(id) {
      this.f1ExpandedId = this.f1ExpandedId === id ? null : id;
    },

    // ── Detail slide (right) ──
    f1OpenDetailSlide(tx) {
      this.f1DetailTx = tx;
      this.f1EditForm = {
        amount:      tx.amount,
        category_id: tx.category_id,
        status:      tx.status || "pending",
        description: tx.description || ""
      };
    },

    // ── Add slide (left) ──
    f1OpenAddSlide() {
      const today = new Date();
      const fmt = d => d.toISOString().substring(0, 10);
      this.f1AddForm = {
        type: "expense", amount: null, category_id: null,
        date: fmt(today), description: "",
        payment_method: null, user_id: null,
        recurring: "One-time",
        currency: "USD", purchase_type: "", reason: "",
        receiptName: "", receiptFile: null, receiptPreview: null
      };
      this.f1AddError = null;
      this.f1AddOpen = true;
    },

    // ── Save edit ──
    async f1SaveEdit() {
      if (!this.f1DetailTx) return;
      this.f1Saving = true;
      try {
        const res = await fetch(`${API}/finance/edit_row/${this.f1DetailTx.id}`, {
          method:  "POST",
          headers: this.f1AuthHeaders(),
          body:    JSON.stringify(this.f1EditForm)
        });
        if (!res.ok) throw new Error("save failed");
        this.f1DetailTx = null;
        this.f1ShowToast("Transaction updated");
        this.f1Fetch();
      } catch (e) {
        this.f1ShowToast("Save failed", "#ef4444");
        console.error("Finance1: save error", e);
      } finally {
        this.f1Saving = false;
      }
    },

    // ── Delete ──
    async f1DeleteTx(id) {
      if (!this.f1IsAdmin) return;
      if (!confirm("Delete this transaction?")) return;
      this.f1Saving = true;
      try {
        const res = await fetch(`${API}/finance/delete_row/${id}`, {
          method: "POST", headers: this.f1AuthHeaders()
        });
        if (!res.ok) throw new Error("delete failed");
        this.f1DetailTx = null;
        this.f1ShowToast("Deleted", "#ef4444");
        this.f1Fetch();
      } catch (e) {
        this.f1ShowToast("Delete failed", "#ef4444");
      } finally {
        this.f1Saving = false;
      }
    },

    // ── Submit new transaction ──
    async f1SubmitAdd() {
      this.f1AddError = null;
      if (!this.f1AddForm.amount || !this.f1AddForm.category_id || !this.f1AddForm.date) return;
      this.f1Saving = true;
      try {
        let body;
        let headers;

        if (this.f1AddForm.receiptFile) {
          // Use FormData to include file upload
          const fd = new FormData();
          fd.append("amount",         this.f1AddForm.amount);
          fd.append("category_id",    this.f1AddForm.category_id);
          fd.append("created_at",     this.f1AddForm.date);
          fd.append("description",    this.f1AddForm.description || "");
          fd.append("type",           this.f1AddForm.type);
          fd.append("recurring",      this.f1AddForm.recurring || "One-time");
          if (this.f1AddForm.payment_method) fd.append("payment_method", this.f1AddForm.payment_method);
          if (this.f1AddForm.user_id) fd.append("user_id", this.f1AddForm.user_id);
          fd.append("receipt", this.f1AddForm.receiptFile);
          body    = fd;
          headers = { Authorization: "Bearer " + this.f1Token };
        } else {
          body = JSON.stringify({
            amount:         this.f1AddForm.amount,
            category_id:    this.f1AddForm.category_id,
            created_at:     this.f1AddForm.date,
            description:    this.f1AddForm.description,
            type:           this.f1AddForm.type,
            recurring:      this.f1AddForm.recurring || "One-time",
            payment_method: this.f1AddForm.payment_method,
            user_id:        this.f1AddForm.user_id,
            currency:       this.f1AddForm.currency || "USD",
            purchase_type:  this.f1AddForm.purchase_type || undefined,
            reason:         this.f1AddForm.reason || undefined
          });
          headers = this.f1AuthHeaders();
        }

        const res = await fetch(`${API}/finance/create_finance`, {
          method: "POST", headers, body
        });
        if (!res.ok) throw new Error("create failed");
        this.f1AddOpen = false;
        this.f1ShowToast("Transaction added");
        this.f1Fetch();
      } catch (e) {
        this.f1AddError = "Failed to add transaction. Please try again.";
        console.error("Finance1: add error", e);
      } finally {
        this.f1Saving = false;
      }
    },

    // ── Receipt file handler ──
    f1HandleReceiptFile(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.f1AddForm.receiptFile = file;
      this.f1AddForm.receiptName = file.name;
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = ev => { this.f1AddForm.receiptPreview = ev.target.result; };
        reader.readAsDataURL(file);
      } else {
        this.f1AddForm.receiptPreview = null;
      }
    },

    f1OpenReceipt(url) { window.open(url, "_blank"); },

    // ── Debounced search ──
    f1DebouncedFetch() {
      clearTimeout(this.f1SearchTimer);
      this.f1SearchTimer = setTimeout(() => this.f1Fetch(), 350);
    },

    // ── Currency helpers ──
    f1CurrencySymbol(currency) {
      const symbols = { USD: '$', EUR: '\u20AC', GBP: '\u00A3', ILS: '\u20AA' };
      return symbols[currency] || symbols['USD'];
    },
    f1FormatAmtCurrency(val, currency) {
      const n = parseFloat(val) || 0;
      const sym = this.f1CurrencySymbol(currency);
      if (n >= 1000000) return sym + (n / 1000000).toFixed(1) + 'M';
      if (n >= 1000)    return sym + (n / 1000).toFixed(1) + 'k';
      return sym + n.toFixed(2);
    },

    // ── Format helpers ──
    f1FormatAmt(val) {
      const n = parseFloat(val) || 0;
      if (n >= 1000000) return "$" + (n / 1000000).toFixed(1) + "M";
      if (n >= 1000)    return "$" + (n / 1000).toFixed(1) + "k";
      return "$" + n.toFixed(2);
    },
    f1FormatAmtShort(val) {
      const n = parseFloat(val) || 0;
      if (n >= 1000) return "$" + Math.round(n / 1000) + "k";
      return "$" + Math.round(n);
    },
    f1FmtDate(ts) {
      if (!ts) return "—";
      const d = new Date(ts);
      return d.toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" });
    },
    f1DayLabel(ts) { return ts ? new Date(ts).getDate() : "—"; },
    f1MonLabel(ts) {
      if (!ts) return "";
      return ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][new Date(ts).getMonth()];
    },
    f1TxUserInitials(tx) {
      if (!tx.user_name) return "?";
      const parts = tx.user_name.split(" ");
      return ((parts[0] || "")[0] || "").toUpperCase() + ((parts[1] || "")[0] || "").toUpperCase();
    },
    f1UserAvatarColor(userId) {
      return this.f1AvatarColors[(userId || 0) % this.f1AvatarColors.length];
    },
    f1CatColor(catId) {
      const palette = ["#6366f1","#f59e0b","#10b981","#ef4444","#8b5cf6","#06b6d4","#f97316","#ec4899","#84cc16","#14b8a6"];
      return palette[(catId || 0) % palette.length];
    },
    f1CatBg(catId)  { return this.f1CatColor(catId) + "22"; },
    f1ApprovalBadgeStyle(status) {
      if (status === "approved") return "background:rgba(74,222,128,.12);color:#16a34a";
      if (status === "rejected") return "background:rgba(248,113,113,.12);color:#dc2626";
      return "background:rgba(251,191,36,.12);color:#b45309";
    },

    // ── CSV Export ──────────────────────────────────────────────────────────
    async f1ExportCSV() {
      if (this.f1ExportLoading) return;
      this.f1ExportLoading = true;
      try {
        const params = new URLSearchParams();
        params.set("per_page", "9999");
        params.set("page", "1");
        if (this.f1Filters.date_from) params.set("date_from", this.f1Filters.date_from);
        if (this.f1Filters.date_to)   params.set("date_to",   this.f1Filters.date_to);
        if (this.f1Filters.category_ids.length === 1) params.set("category_id", this.f1Filters.category_ids[0]);
        if (this.f1Filters.category_ids.length > 1)  params.set("category_ids", this.f1Filters.category_ids.join(","));
        if (this.f1Filters.user_id)        params.set("user_id",        this.f1Filters.user_id);
        if (this.f1Filters.payment_method) params.set("payment_method", this.f1Filters.payment_method);
        if (this.f1Filters.status)         params.set("status",         this.f1Filters.status);
        if (this.f1Filters.search)         params.set("search",         this.f1Filters.search);

        const res = await fetch(`${API}/finance/list?${params.toString()}`, { headers: this.f1AuthHeaders() });
        if (!res.ok) throw new Error("Export fetch failed");
        const json = await res.json();
        const data = json.data || json;
        const rows = Array.isArray(data) ? data : (data.data || []);

        const headers = ['ID', 'Date', 'Type', 'Amount', 'Currency', 'Category', 'Payment Method', 'Status', 'Description', 'User', 'Created At'];
        const csvRows = rows.map(tx => [
          tx.id || '',
          (tx.created_at || '').slice(0, 10),
          tx.type || '',
          tx.amount || '',
          tx.currency || 'USD',
          tx.category_name || (tx.category && tx.category.name) || '',
          tx.payment_method || '',
          tx.status || '',
          String(tx.description || '').replace(/"/g, '""'),
          tx.user_name || (tx.user && tx.user.full_name) || '',
          (tx.created_at || '').slice(0, 19).replace('T', ' ')
        ]);

        const csv = [headers, ...csvRows].map(r => r.map(v => `"${String(v)}"`).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `finance-export-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        this.f1ShowToast('CSV exported (' + rows.length + ' rows)', '#4ade80');
      } catch (e) {
        console.error('Finance CSV export failed', e);
        this.f1ShowToast('Export failed', '#ef4444');
      } finally {
        this.f1ExportLoading = false;
      }
    }
  }
};
</script>

<style scoped>
/* ═══════════════════════════════════════
   Finance1 — full scoped stylesheet
═══════════════════════════════════════ */

/* Hide the right-side panel — replaced by top strip */
.c1-finance-page .c1-panel { display: none !important; }


/* ── Design tokens ── */
:root,
.c1-main {
  --c1-bg:       #f4f3f0;
  --c1-surface:  #ffffff;
  --c1-surface2: #f4f3f0;
  --c1-border:   #e4e3df;
  --c1-text:     #1a1a1a;
  --c1-muted:    #a09e98;
  --c1-accent:   #6366f1;
  --c1-accent2:  #8b5cf6;
  --c1-dp:       #22c55e;
  --c1-ld:       #f59e0b;
  --c1-na:       #ef4444;
  --c1-sc:       #3b82f6;
  --c1-rf:       #a855f7;
  --f1-t1:       var(--c1-text);
  --f1-t2:       #6b6963;
  --f1-t3:       #a09e98;
}

/* ── Root layout ── */
.c1-main {
  display: flex;
  flex-direction: column;
  height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  position: relative;
  overflow: hidden;
}

/* ── Toast ── */
.fi1-toast {
  position: fixed;
  bottom: 28px; left: 50%;
  transform: translateX(-50%);
  z-index: 99999;
  padding: 10px 22px;
  border-radius: 24px;
  font-size: .85rem;
  font-weight: 600;
  color: #fff;
  pointer-events: none;
  box-shadow: 0 4px 20px rgba(0,0,0,.25);
  white-space: nowrap;
  height: auto;
  display: inline-flex;
  align-items: center;
}
.fi1-toast-fade-enter-active,
.fi1-toast-fade-leave-active { transition: opacity .25s, transform .25s; }
.fi1-toast-fade-enter,
.fi1-toast-fade-leave-to { opacity: 0; transform: translateX(-50%) translateY(10px); }

/* Export CSV button */
.c1-tib-icon-btn {
  background: none;
  border: 1px solid rgba(255,255,255,.15);
  border-radius: 6px;
  padding: 5px 7px;
  cursor: pointer;
  color: inherit;
  display: flex;
  align-items: center;
  transition: background .15s;
  flex-shrink: 0;
  margin-right: 4px;
}
.c1-tib-icon-btn:hover { background: rgba(255,255,255,.08); }
.c1-tib-icon-btn.loading { opacity: .5; pointer-events: none; }

/* Add button */
.f1-add-btn {
  display: flex; align-items: center; gap: 5px;
  padding: 0 12px; height: 32px;
  background: var(--c1-accent);
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px; font-weight: 700; color: #fff;
  flex-shrink: 0;
  transition: opacity .15s;
  margin-right: 4px;
}
.f1-add-btn:active { opacity: .8; }
.f1-add-btn svg { width: 13px; height: 13px; stroke: #fff; stroke-width: 2.5; fill: none; }

/* .c1-panel-backdrop — defined globally in c1-design.css */

/* ── Panels (global c1-panel handles fixed sliding drawer) ── */
.c1-panel {
  background: var(--c1-surface);
  border-left: 1px solid var(--c1-border);
}
.c1-prof-panel {
  position: fixed;
  top: 44px; right: 0; bottom: 0;
  width: min(340px, 96vw);
  background: var(--c1-surface);
  border-left: 1px solid var(--c1-border);
  z-index: 300;
  display: flex; flex-direction: column;
  overflow: hidden;
}
.c1-cal-panel  { width: min(300px, 96vw); }
.f1-viki-panel { width: min(360px, 96vw); }
.c1-prof-panel { width: min(260px, 94vw); }

.c1-panel-hdr-bar {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--c1-border);
  flex-shrink: 0;
}
.c1-panel-hdr-bar svg { width: 15px; height: 15px; stroke: var(--c1-accent); stroke-width: 2; fill: none; flex-shrink: 0; }
.c1-panel-hdr-ttl { font-size: 13px; font-weight: 700; color: var(--c1-text); flex: 1; }
.c1-panel-hdr-count { font-size: 10px; color: var(--c1-accent); font-weight: 600; }
.c1-collapse-btn {
  width: 28px; height: 28px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 6px;
  cursor: pointer;
  color: var(--f1-t3);
  transition: background .1s;
  flex-shrink: 0;
}
.c1-collapse-btn:hover { background: var(--c1-border); }
.c1-collapse-btn svg { width: 14px; height: 14px; stroke: currentColor; stroke-width: 2; fill: none; }

.c1-reset-bar {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 14px;
  background: rgba(239,68,68,.08);
  border-bottom: 1px solid rgba(239,68,68,.2);
  cursor: pointer;
  font-size: 11px; font-weight: 600; color: #f87171;
  flex-shrink: 0;
}
.c1-reset-bar svg { width: 12px; height: 12px; stroke: currentColor; stroke-width: 2; fill: none; }
.c1-reset-bar-count {
  background: #f87171; color: #fff;
  font-size: 9px; font-weight: 700;
  padding: 1px 5px; border-radius: 8px;
}

.c1-sl-body { flex: 1; overflow-y: auto; padding: 12px; }
.c1-sl-body::-webkit-scrollbar { width: 4px; }
.c1-sl-body::-webkit-scrollbar-thumb { background: var(--c1-border); border-radius: 2px; }

.c1-collapse-bar {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  padding: 10px;
  border-top: 1px solid var(--c1-border);
  cursor: pointer;
  font-size: 11px; font-weight: 600; color: var(--f1-t3);
  flex-shrink: 0;
  transition: background .1s;
}
.c1-collapse-bar:hover { background: var(--c1-border); }
.c1-collapse-bar svg { width: 12px; height: 12px; stroke: currentColor; stroke-width: 2; fill: none; }
.c1-collapse-bar-lg { padding: 12px; }

/* ── Filter panel UI ── */
.c1-fp-group { display: flex; flex-direction: column; gap: 6px; margin-bottom: 12px; }
.c1-fp-full  { width: 100%; }
.c1-fp-lbl   { font-size: 9px; color: var(--f1-t3); text-transform: uppercase; letter-spacing: .06em; font-weight: 700; }
.c1-fp-opts  { display: flex; flex-wrap: wrap; gap: 5px; }
.c1-fp-opt {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px; font-weight: 600;
  border: 1px solid var(--c1-border);
  color: var(--f1-t2);
  cursor: pointer;
  transition: all .12s;
  user-select: none;
}
.c1-fp-opt:hover { border-color: var(--c1-accent); color: var(--c1-accent); }
.c1-fp-opt.on { background: rgba(99,102,241,.15); color: var(--c1-accent); border-color: var(--c1-accent); }

.c1-date-range-row { display: flex; align-items: center; gap: 6px; }
.c1-date-input {
  flex: 1;
  background: var(--c1-bg);
  border: 1px solid var(--c1-border);
  border-radius: 6px;
  color: var(--c1-text);
  padding: 5px 8px;
  font-size: 11px;
}
.c1-date-input:focus { outline: none; border-color: var(--c1-accent); }
.c1-date-sep { font-size: 11px; color: var(--f1-t3); }

.f1-range-row { display: flex; align-items: center; gap: 6px; }
.f1-range-input { flex: 1; max-width: 120px; }

/* Search input */
.c1-fp-search { position: relative; display: flex; align-items: center; }
.c1-fp-sico { position: absolute; left: 8px; width: 13px; height: 13px; stroke: var(--f1-t3); stroke-width: 2; fill: none; pointer-events: none; }
.c1-fp-sinput {
  width: 100%;
  background: var(--c1-bg);
  border: 1px solid var(--c1-border);
  border-radius: 8px;
  padding: 7px 30px 7px 28px;
  font-size: 12px; color: var(--c1-text);
}
.c1-fp-sinput::placeholder { color: var(--f1-t3); }
.c1-fp-sinput:focus { outline: none; border-color: var(--c1-accent); }
.c1-fp-sclear {
  position: absolute; right: 6px;
  width: 20px; height: 20px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; border-radius: 4px;
  color: var(--f1-t3);
}
.c1-fp-sclear svg { width: 12px; height: 12px; stroke: currentColor; stroke-width: 2; fill: none; }

/* Status filter tints */
.f1-status-approved.on { background: rgba(74,222,128,.12); color: #16a34a; border-color: #4ade80; }
.f1-status-pending.on  { background: rgba(251,191,36,.12);  color: #b45309; border-color: #fbbf24; }
.f1-status-rejected.on { background: rgba(248,113,113,.12); color: #dc2626; border-color: #f87171; }

/* ── Charts ── */
.c1-co-lbl {
  font-size: 9px; font-weight: 700;
  color: var(--f1-t3);
  text-transform: uppercase; letter-spacing: .06em;
  margin-bottom: 8px;
}

/* Donut */
.f1-donut-wrap {
  position: relative;
  display: flex; justify-content: center; align-items: center;
  height: 140px;
}
.f1-donut-svg { width: 140px; height: 140px; }
.f1-donut-center {
  position: absolute;
  display: flex; flex-direction: column; align-items: center;
  pointer-events: none;
}
.f1-donut-total { font-size: 15px; font-weight: 700; color: var(--c1-text); }
.f1-donut-lbl   { font-size: 9px; color: var(--f1-t3); text-transform: uppercase; letter-spacing: .04em; }
.f1-donut-legend { margin-top: 10px; display: flex; flex-direction: column; gap: 5px; }
.f1-leg-row { display: flex; align-items: center; gap: 6px; font-size: 11px; }
.f1-leg-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.f1-leg-name { flex: 1; color: var(--f1-t1); }
.f1-leg-val  { color: var(--c1-text); font-weight: 600; }
.f1-leg-pct  { color: var(--f1-t3); width: 34px; text-align: right; }

/* Line chart */
.f1-line-wrap { background: var(--c1-bg); border-radius: 8px; padding: 8px; border: 1px solid var(--c1-border); }
.f1-line-svg  { width: 100%; height: 80px; display: block; }
.f1-line-labels { display: flex; align-items: center; font-size: 9px; color: var(--f1-t3); margin-top: 4px; }

/* Budget bars */
.f1-budget-rows { display: flex; flex-direction: column; gap: 10px; }
.f1-budget-row  { display: flex; flex-direction: column; gap: 3px; }
.f1-budget-name { font-size: 11px; color: var(--f1-t2); }
.f1-budget-track { height: 5px; background: var(--c1-border); border-radius: 3px; overflow: hidden; }
.f1-budget-fill { height: 100%; border-radius: 3px; transition: width .3s; }
.f1-budget-vals { display: flex; align-items: center; gap: 3px; font-size: 10px; }
.f1-budget-over  { color: #f87171; font-weight: 700; }
.f1-budget-ok    { color: #4ade80; font-weight: 700; }
.f1-budget-sep   { color: var(--f1-t3); }
.f1-budget-limit { color: var(--f1-t3); }
.f1-budget-pct   { margin-left: auto; }

/* Profitability cards */
.f1-profit-empty { font-size: 11px; color: var(--f1-t3); padding: 10px 0; text-align: center; }
.f1-profit-card {
  background: var(--c1-bg);
  border: 1px solid var(--c1-border);
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 8px;
}
.f1-profit-name { font-size: 12px; font-weight: 700; color: var(--c1-text); margin-bottom: 8px; }
.f1-profit-row  { display: flex; gap: 4px; margin-bottom: 8px; }
.f1-profit-kpi  { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 2px; }
.f1-profit-kv   { font-size: 12px; font-weight: 700; }
.f1-profit-kl   { font-size: 8px; color: var(--f1-t3); text-transform: uppercase; letter-spacing: .04em; }
.f1-pk-spend    { color: #f87171; }
.f1-pk-rev      { color: #60a5fa; }
.f1-pk-profit   { color: #4ade80; }
.f1-pk-loss     { color: #f87171; }
.f1-profit-bar-track { height: 4px; background: var(--c1-border); border-radius: 2px; overflow: hidden; margin-bottom: 6px; }
.f1-profit-bar-fill  { height: 100%; border-radius: 2px; transition: width .3s; }
.f1-profit-detail    { font-size: 9px; color: var(--f1-t3); }

/* MRR */
.f1-mrr-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-bottom: 8px; }
.f1-mrr-card {
  background: var(--c1-bg);
  border: 1px solid var(--c1-border);
  border-radius: 8px;
  padding: 8px 10px;
  display: flex; flex-direction: column; gap: 2px;
}
.f1-mrr-plan  { font-size: 10px; font-weight: 700; color: var(--c1-text); }
.f1-mrr-price { font-size: 9px; color: var(--c1-accent); }
.f1-mrr-count { font-size: 9px; color: var(--f1-t3); }
.f1-mrr-total { font-size: 12px; font-weight: 700; color: #4ade80; margin-top: 2px; }
.f1-mrr-total-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 10px;
  background: rgba(99,102,241,.08);
  border: 1px solid rgba(99,102,241,.2);
  border-radius: 8px;
  margin-top: 4px;
}
.f1-mrr-total-lbl { font-size: 11px; font-weight: 700; color: var(--f1-t2); }
.f1-mrr-total-val { font-size: 16px; font-weight: 800; color: var(--c1-accent); }

/* ── Calendar ── */
.f1-cal-nav { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
.f1-cal-title { flex: 1; text-align: center; font-size: 13px; font-weight: 600; color: var(--c1-text); }
.f1-cal-nav-btn {
  width: 28px; height: 28px;
  background: var(--c1-border); border: none; border-radius: 6px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: var(--f1-t2); flex-shrink: 0;
}
.f1-cal-nav-btn svg { width: 14px; height: 14px; stroke: currentColor; stroke-width: 2; fill: none; }
.f1-cal-nav-btn:active { opacity: .7; }

.f1-cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
.f1-cal-dow  { font-size: 8px; color: var(--f1-t3); text-align: center; padding: 3px 0; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; }
.f1-cal-cell {
  position: relative;
  aspect-ratio: 1;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  border-radius: 6px; cursor: pointer;
  transition: background .1s;
  gap: 1px;
}
.f1-cal-cell:hover { background: rgba(99,102,241,.12); }
.f1-cal-cell.out   { opacity: .3; pointer-events: none; }
.f1-cal-cell.today .f1-cal-day { color: var(--c1-accent); font-weight: 700; }
.f1-cal-cell.selected { background: rgba(99,102,241,.2); outline: 1px solid var(--c1-accent); }
.f1-cal-cell.cal-over  { background: rgba(239,68,68,.06); }
.f1-cal-cell.cal-ok    { background: rgba(74,222,128,.04); }
.f1-cal-day  { font-size: 11px; color: var(--f1-t1); line-height: 1; }
.f1-cal-amt  { font-size: 7px; color: var(--f1-t3); line-height: 1; }
.f1-cal-dot  { width: 4px; height: 4px; border-radius: 50%; background: var(--c1-accent); }
.f1-cal-dot.dot-over { background: #f87171; }

.f1-cal-day-summary {
  margin-top: 12px;
  background: rgba(99,102,241,.08);
  border: 1px solid rgba(99,102,241,.25);
  border-radius: 8px;
  padding: 10px 12px;
  display: flex; flex-direction: column; align-items: center; gap: 3px;
}
.f1-cds-label { font-size: 10px; color: var(--f1-t3); font-family: monospace; }
.f1-cds-total { font-size: 20px; font-weight: 700; color: var(--c1-accent); }
.f1-cds-count { font-size: 10px; color: var(--f1-t2); }
.f1-cds-clear { margin-top: 6px; font-size: 10px; color: var(--c1-accent); background: none; border: none; cursor: pointer; text-decoration: underline; }

.f1-cal-legend { display: flex; gap: 12px; justify-content: center; margin-top: 10px; }
.f1-cal-leg-item { display: flex; align-items: center; gap: 4px; font-size: 9px; color: var(--f1-t3); }
.f1-cal-leg-dot  { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }

/* ── Viki panel ── */
.f1-viki-budget-box {
  background: var(--c1-bg);
  border: 1px solid var(--c1-border);
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 14px;
}
.f1-viki-budget-title { font-size: 10px; font-weight: 700; color: var(--f1-t3); text-transform: uppercase; letter-spacing: .06em; margin-bottom: 8px; }
.f1-viki-budget-row   { display: flex; align-items: baseline; gap: 6px; margin-bottom: 8px; }
.f1-viki-spent        { font-size: 24px; font-weight: 800; color: var(--c1-text); }
.f1-viki-budget-sep   { font-size: 12px; color: var(--f1-t3); }
.f1-viki-budget-limit { font-size: 14px; font-weight: 600; color: var(--f1-t2); }
.f1-viki-budget-track { height: 6px; background: var(--c1-border); border-radius: 3px; overflow: hidden; margin-bottom: 6px; }
.f1-viki-budget-fill  { height: 100%; border-radius: 3px; transition: width .4s; }
.f1-viki-budget-pct   { font-size: 10px; font-weight: 700; }
.f1-over { color: #f87171; }
.f1-ok   { color: #4ade80; }

.f1-viki-section-lbl { font-size: 9px; font-weight: 700; color: var(--f1-t3); text-transform: uppercase; letter-spacing: .06em; margin-bottom: 8px; }

.f1-viki-noalerts {
  display: flex; align-items: center; gap: 6px;
  font-size: 11px; color: #4ade80;
  padding: 8px 10px;
  background: rgba(74,222,128,.06);
  border: 1px solid rgba(74,222,128,.15);
  border-radius: 8px;
  margin-bottom: 8px;
}
.f1-viki-noalerts svg { width: 14px; height: 14px; stroke: #4ade80; stroke-width: 2.5; fill: none; flex-shrink: 0; }

.f1-viki-alert {
  display: flex; align-items: flex-start; gap: 8px;
  padding: 9px 10px;
  border-radius: 8px;
  margin-bottom: 6px;
  border: 1px solid;
}
.f1-viki-warn { background: rgba(245,158,11,.06); border-color: rgba(245,158,11,.25); }
.f1-viki-info { background: rgba(99,102,241,.06); border-color: rgba(99,102,241,.2); }
.f1-viki-alert-icon svg { width: 16px; height: 16px; fill: none; stroke-width: 2; flex-shrink: 0; }
.f1-viki-warn .f1-viki-alert-icon svg { stroke: #f59e0b; }
.f1-viki-info .f1-viki-alert-icon svg { stroke: var(--c1-accent); }
.f1-viki-alert-body  { flex: 1; }
.f1-viki-alert-msg   { font-size: 11px; font-weight: 600; color: var(--c1-text); line-height: 1.4; }
.f1-viki-alert-meta  { font-size: 9px; color: var(--f1-t3); margin-top: 2px; }

.f1-viki-none { font-size: 11px; color: var(--f1-t3); padding: 6px 0; }
.f1-viki-tx-row {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 8px; border-radius: 8px;
  cursor: pointer; margin-bottom: 3px;
  transition: background .1s;
}
.f1-viki-tx-row:hover { background: var(--c1-border); }
.f1-viki-tx-desc { flex: 1; font-size: 11px; color: var(--f1-t1); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.f1-viki-tx-amt  { font-size: 12px; font-weight: 700; flex-shrink: 0; }
.f1-viki-tx-amt.expense { color: #f87171; }

.f1-viki-week-row { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; }
.f1-viki-wk-name  { font-size: 10px; color: var(--f1-t2); width: 70px; flex-shrink: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.f1-viki-wk-track { flex: 1; height: 4px; background: var(--c1-border); border-radius: 2px; overflow: hidden; }
.f1-viki-wk-fill  { height: 100%; border-radius: 2px; }
.f1-viki-wk-val   { font-size: 10px; font-weight: 600; color: var(--f1-t1); width: 52px; text-align: right; flex-shrink: 0; }

/* ── Profile panel ── */
.c1-prof-head { padding: 16px 14px; border-bottom: 1px solid var(--c1-border); }
.c1-prof-brand-row { display: flex; align-items: center; margin-bottom: 10px; }
.c1-prof-brand-name { font-size: 16px; font-weight: 800; color: var(--c1-text); letter-spacing: -.02em; }
.c1-prof-brand-name span { color: var(--c1-accent); }
.c1-prof-user-row { display: flex; align-items: center; gap: 10px; }
.c1-prof-av { width: 36px; height: 36px; border-radius: 50%; background: rgba(99,102,241,.2); display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 700; color: var(--c1-accent); flex-shrink: 0; }
.c1-prof-name { font-size: 13px; font-weight: 700; color: var(--c1-text); }
.c1-prof-role { font-size: 10px; color: var(--f1-t3); }
.c1-prof-scroll { flex: 1; overflow-y: auto; padding: 8px 0 20px; }
.c1-nc-lbl { font-size: 9px; color: var(--f1-t3); text-transform: uppercase; letter-spacing: .06em; font-weight: 700; padding: 10px 14px 4px; }
.c1-ng { display: flex; flex-direction: column; gap: 1px; padding: 0 8px; }
.c1-nb {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 10px; border-radius: 8px;
  font-size: 12px; font-weight: 600; color: var(--f1-t2);
  text-decoration: none; cursor: pointer;
  transition: background .1s, color .1s;
}
.c1-nb:hover { background: var(--c1-border); color: var(--c1-text); }
.c1-nb svg { width: 14px; height: 14px; stroke: currentColor; stroke-width: 2; fill: none; flex-shrink: 0; }
.c1-nb-logout { color: #f87171; }
.c1-nb-logout:hover { background: rgba(248,113,113,.1); }

/* ── Summary bar ── */
.f1-summary-bar {
  display: flex; align-items: center;
  background: var(--c1-surface);
  border-bottom: 1px solid var(--c1-border);
  padding: 0 16px; height: 52px; gap: 0;
  overflow-x: auto; scrollbar-width: none; flex-shrink: 0;
}
.f1-summary-bar::-webkit-scrollbar { display: none; }
.f1-kpi { display: flex; flex-direction: column; align-items: center; padding: 0 12px; min-width: 72px; }
.f1-kpi-val { font-size: 13px; font-weight: 700; color: var(--c1-text); line-height: 1; white-space: nowrap; }
.f1-kpi-lbl { font-size: 8px; color: var(--f1-t3); margin-top: 3px; white-space: nowrap; text-transform: uppercase; letter-spacing: .04em; }
.f1-kpi-sep { width: 1px; height: 24px; background: var(--c1-border); flex-shrink: 0; }
.f1-kpi-income  { color: #4ade80; }
.f1-kpi-expense { color: #f87171; }

/* ── Transaction list ── */
.f1-list {
  flex: 1; overflow-y: auto; padding: 8px 0 80px;
}
.f1-list::-webkit-scrollbar { width: 4px; }
.f1-list::-webkit-scrollbar-thumb { background: var(--c1-border); border-radius: 2px; }

.f1-tx-box {
  background: var(--c1-surface);
  border: 1px solid var(--c1-border);
  border-radius: 10px;
  margin: 5px 10px;
  overflow: hidden;
  transition: border-color .15s;
}
.f1-tx-box.expanded { border-color: var(--c1-accent); }

.f1-tx-row {
  display: flex; align-items: center;
  padding: 10px 12px; gap: 10px;
  cursor: pointer; position: relative; user-select: none;
}
.f1-tx-row:active { background: rgba(99,102,241,.05); }

.f1-tx-date { display: flex; flex-direction: column; align-items: center; width: 30px; flex-shrink: 0; }
.f1-tx-day  { font-size: 16px; font-weight: 700; color: var(--c1-text); line-height: 1; }
.f1-tx-mon  { font-size: 8px; color: var(--f1-t3); text-transform: uppercase; letter-spacing: .04em; }

.f1-tx-mid   { flex: 1; min-width: 0; }
.f1-tx-desc  { font-size: 13px; font-weight: 600; color: var(--f1-t1); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.f1-tx-meta  { display: flex; align-items: center; gap: 5px; margin-top: 3px; flex-wrap: wrap; }
.f1-tx-proj  { font-size: 10px; color: var(--f1-t3); }

.f1-cat-chip {
  display: inline-block;
  font-size: 10px; font-weight: 600;
  padding: 2px 7px; border-radius: 20px;
  letter-spacing: .02em;
}
.f1-recur-badge {
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 9px; font-weight: 600;
  color: var(--c1-accent);
  background: rgba(99,102,241,.1);
  padding: 1px 6px; border-radius: 10px;
}
.f1-recur-badge svg { width: 9px; height: 9px; stroke: currentColor; stroke-width: 2; fill: none; }

.f1-tx-right { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; flex-shrink: 0; }
.f1-tx-amount { font-size: 15px; font-weight: 700; letter-spacing: -.01em; }
.f1-tx-amount.expense { color: #f87171; }
.f1-tx-amount.income  { color: #4ade80; }
.f1-tx-status-row { display: flex; align-items: center; gap: 5px; }

.f1-status-chip {
  display: inline-block;
  font-size: 9px; font-weight: 700;
  padding: 2px 6px; border-radius: 20px;
  text-transform: uppercase; letter-spacing: .04em;
}
.f1-status-approved { background: rgba(74,222,128,.12); color: #16a34a; }
.f1-status-pending  { background: rgba(251,191,36,.12);  color: #b45309; }
.f1-status-rejected { background: rgba(248,113,113,.12); color: #dc2626; }

.f1-tx-av {
  width: 22px; height: 22px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 8px; font-weight: 700; color: #fff; flex-shrink: 0;
}
.f1-tx-chv { width: 14px; height: 14px; color: var(--f1-t3); flex-shrink: 0; transition: transform .2s; margin-left: 2px; }
.f1-tx-chv.open { transform: rotate(180deg); color: var(--c1-accent); }

/* ── Expanded detail ── */
.f1-tx-detail { border-top: 1px solid var(--c1-border); padding: 12px; background: var(--c1-bg); }

.f1-td-grid {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 8px; margin-bottom: 8px;
}
.f1-td-field { display: flex; flex-direction: column; gap: 2px; }
.f1-td-full  { grid-column: 1 / -1; }
.f1-td-lbl   { font-size: 9px; color: var(--f1-t3); text-transform: uppercase; letter-spacing: .04em; }
.f1-td-val   { font-size: 12px; color: var(--f1-t1); font-weight: 500; }
.f1-td-big   { font-size: 18px; font-weight: 700; }
.f1-td-big.expense { color: #f87171; }
.f1-td-big.income  { color: #4ade80; }
.f1-td-note  { font-size: 11px; color: var(--f1-t2); line-height: 1.5; }

.f1-td-receipt-slot { margin-top: 8px; }
.f1-receipt-thumb {
  max-width: 100%; max-height: 120px;
  object-fit: cover; border-radius: 6px;
  border: 1px solid var(--c1-border); cursor: pointer;
  display: block;
}
.f1-receipt-hint { font-size: 9px; color: var(--f1-t3); margin-top: 3px; }

.f1-td-history { border-top: 1px solid var(--c1-border); padding-top: 8px; margin-top: 8px; }
.f1-td-hist-lbl { font-size: 9px; color: var(--f1-t3); text-transform: uppercase; letter-spacing: .04em; margin-bottom: 6px; }
.f1-td-hist-row { display: flex; align-items: center; gap: 6px; margin-bottom: 4px; }
.f1-td-hist-by  { font-size: 11px; color: var(--f1-t1); flex: 1; }
.f1-td-hist-dt  { font-size: 10px; color: var(--f1-t3); }

.f1-td-actions { display: flex; gap: 6px; margin-top: 10px; border-top: 1px solid var(--c1-border); padding-top: 10px; }
.f1-td-btn {
  display: flex; align-items: center; gap: 5px;
  padding: 6px 12px; border-radius: 8px;
  font-size: 11px; font-weight: 600;
  border: none; cursor: pointer; transition: opacity .15s;
}
.f1-td-btn:active { opacity: .7; }
.f1-td-btn svg { width: 12px; height: 12px; stroke: currentColor; stroke-width: 2; fill: none; }
.f1-td-btn-receipt { background: var(--c1-border); color: var(--f1-t1); }
.f1-td-btn-detail  { background: rgba(6,182,212,.1); color: #06b6d4; }
.f1-td-btn-edit    { background: rgba(99,102,241,.15); color: var(--c1-accent); }

/* ── Right drawer ── */
.c1-drawer-wrap {
  position: fixed; inset: 0;
  z-index: 400;
  background: rgba(0,0,0,.5);
  display: flex; justify-content: flex-end;
}
.c1-drawer {
  width: min(400px, 96vw);
  height: 100%;
  background: var(--c1-surface);
  border-left: 1px solid var(--c1-border);
  display: flex; flex-direction: column;
  overflow: hidden;
}
.c1-drawer-head {
  display: flex; align-items: center; gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--c1-border);
  flex-shrink: 0;
}
.c1-drawer-title { flex: 1; min-width: 0; }
.c1-drawer-name  { font-size: 15px; font-weight: 700; color: var(--c1-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.c1-drawer-sub   { font-size: 10px; color: var(--f1-t3); font-family: monospace; }
.c1-drawer-close {
  width: 30px; height: 30px;
  background: var(--c1-border); border: none; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: var(--f1-t2); flex-shrink: 0;
}
.c1-drawer-close svg { width: 14px; height: 14px; }
.c1-drawer-body { flex: 1; overflow-y: auto; padding: 16px; }
.c1-drawer-body::-webkit-scrollbar { width: 4px; }
.c1-drawer-body::-webkit-scrollbar-thumb { background: var(--c1-border); border-radius: 2px; }

/* Drawer transitions */
.c1-drawer-enter-active,
.c1-drawer-leave-active { transition: opacity .2s; }
.c1-drawer-enter,
.c1-drawer-leave-to { opacity: 0; }
.c1-drawer-enter-active .c1-drawer,
.c1-drawer-leave-active .c1-drawer { transition: transform .25s cubic-bezier(.4,0,.2,1); }
.c1-drawer-enter .c1-drawer,
.c1-drawer-leave-to .c1-drawer { transform: translateX(100%); }

/* Left drawer */
.f1-drawer-left-wrap .c1-drawer { left: 0; right: auto; border-left: none; border-right: 1px solid var(--c1-border); justify-content: flex-start; }
.f1-add-drawer { max-width: 380px; }
.f1-drawer-left-enter-active, .f1-drawer-left-leave-active { transition: transform .25s cubic-bezier(.4,0,.2,1); }
.f1-drawer-left-enter, .f1-drawer-left-leave-to { transform: translateX(-100%); }

/* Drawer content sections */
.c1-exp-sec { margin-bottom: 18px; }
.c1-exp-lbl { font-size: 9px; font-weight: 700; color: var(--f1-t3); text-transform: uppercase; letter-spacing: .06em; margin-bottom: 8px; }
.c1-exp-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.c1-exp-field { display: flex; flex-direction: column; gap: 3px; }
.c1-exp-full  { grid-column: 1 / -1; }
.c1-exp-fl { font-size: 9px; color: var(--f1-t3); text-transform: uppercase; letter-spacing: .04em; }
.c1-exp-fv { font-size: 12px; color: var(--c1-text); font-weight: 500; }

.c1-exp-activity-row { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.c1-exp-a-badge {
  font-size: 9px; font-weight: 700;
  padding: 3px 7px; border-radius: 6px;
  flex-shrink: 0;
}
.c1-exp-a-body { flex: 1; display: flex; gap: 6px; align-items: center; font-size: 11px; color: var(--f1-t1); }
.c1-exp-a-meta { font-size: 10px; color: var(--f1-t3); }
.c1-exp-a-date { font-size: 10px; color: var(--f1-t3); }

/* Receipt in right drawer */
.f1-receipt-full {
  width: 100%; max-height: 200px;
  object-fit: contain; border-radius: 8px;
  border: 1px solid var(--c1-border); cursor: pointer;
}

/* ── Form elements ── */
.f1-edit-form { display: flex; flex-direction: column; gap: 12px; }

.c1-pkf-group { display: flex; flex-direction: column; gap: 5px; }
.c1-pkf-lbl   { font-size: 10px; font-weight: 700; color: var(--f1-t3); text-transform: uppercase; letter-spacing: .04em; }
.c1-pkf-input, .c1-pkf-select {
  width: 100%;
  background: var(--c1-bg);
  border: 1px solid var(--c1-border);
  border-radius: 8px;
  color: var(--c1-text);
  padding: 8px 10px;
  font-size: 12px;
  box-sizing: border-box;
}
.c1-pkf-input:focus, .c1-pkf-select:focus { outline: none; border-color: var(--c1-accent); }
.c1-pkf-opts { display: flex; flex-wrap: wrap; gap: 5px; }
.c1-pkf-opt {
  padding: 5px 10px; border-radius: 8px;
  font-size: 11px; font-weight: 600;
  background: var(--c1-border); border: 1px solid var(--c1-border);
  color: var(--f1-t2); cursor: pointer; transition: all .12s;
}
.c1-pkf-opt:hover { border-color: var(--c1-accent); color: var(--c1-accent); }
.c1-pkf-opt.on    { background: rgba(99,102,241,.15); color: var(--c1-accent); border-color: var(--c1-accent); }
.c1-pkf-submit {
  width: 100%;
  background: var(--c1-accent); color: #fff;
  border: none; border-radius: 10px;
  padding: 11px 16px; font-size: 13px; font-weight: 700;
  cursor: pointer; transition: opacity .15s;
}
.c1-pkf-submit:hover   { opacity: .9; }
.c1-pkf-submit:disabled { opacity: .5; cursor: not-allowed; }

/* Type toggle */
.f1-type-expense.on { background: rgba(248,113,113,.15) !important; color: #f87171 !important; border-color: #f87171 !important; }
.f1-type-income.on  { background: rgba(74,222,128,.12) !important;  color: #4ade80 !important; border-color: #4ade80 !important; }

/* Delete button */
.f1-delete-btn {
  display: flex; align-items: center; gap: 5px;
  padding: 6px 12px; border-radius: 8px;
  font-size: 11px; font-weight: 600;
  background: rgba(239,68,68,.1); color: #f87171;
  border: 1px solid rgba(239,68,68,.25); cursor: pointer;
  transition: opacity .15s;
}
.f1-delete-btn:hover   { opacity: .8; }
.f1-delete-btn:disabled { opacity: .4; cursor: not-allowed; }
.f1-delete-btn svg { width: 12px; height: 12px; stroke: currentColor; stroke-width: 2; fill: none; }

/* Receipt upload */
.f1-receipt-upload {
  display: flex; align-items: center; gap: 8px;
  background: var(--c1-border);
  border: 1px dashed #c8c6c2; border-radius: 8px;
  padding: 9px 12px; cursor: pointer;
  font-size: 12px; color: var(--f1-t2);
  transition: border-color .15s;
}
.f1-receipt-upload:hover { border-color: var(--c1-accent); color: var(--f1-t1); }
.f1-receipt-upload svg { width: 14px; height: 14px; stroke: currentColor; stroke-width: 2; fill: none; flex-shrink: 0; }
.f1-receipt-preview-wrap { margin-top: 6px; }
.f1-receipt-preview { max-width: 100%; max-height: 100px; border-radius: 6px; border: 1px solid var(--c1-border); }

/* Add error */
.f1-add-error {
  font-size: 11px; color: #f87171;
  background: rgba(248,113,113,.1);
  border: 1px solid rgba(248,113,113,.25);
  border-radius: 6px; padding: 7px 10px;
}

/* ── Pagination ── */
.c1-pg-bar {
  display: flex; align-items: center; gap: 2px;
  padding: 10px 12px;
  border-top: 1px solid var(--c1-border);
  background: var(--c1-surface);
  flex-shrink: 0;
}
.c1-pg-btn {
  width: 30px; height: 30px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 6px; cursor: pointer; color: var(--f1-t2);
  transition: background .1s;
}
.c1-pg-btn svg { width: 14px; height: 14px; stroke: currentColor; stroke-width: 2; fill: none; }
.c1-pg-btn:hover { background: var(--c1-border); color: var(--c1-text); }
.c1-pg-btn.disabled { opacity: .3; pointer-events: none; }
.c1-pg-info { font-size: 11px; color: var(--f1-t2); font-family: monospace; padding: 0 8px; }

/* ── Loading / empty ── */
.c1-loading {
  flex: 1; display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 8px;
}
.c1-spinner {
  width: 28px; height: 28px;
  border: 3px solid var(--c1-border);
  border-top-color: var(--c1-accent);
  border-radius: 50%;
  animation: f1spin .7s linear infinite;
}
@keyframes f1spin { to { transform: rotate(360deg); } }

.c1-empty {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 60px 20px; gap: 8px;
}
.c1-empty-icon { width: 52px; height: 52px; border-radius: 50%; background: var(--c1-surface2); display: flex; align-items: center; justify-content: center; }
.c1-empty-icon svg { width: 24px; height: 24px; stroke: var(--f1-t3); stroke-width: 1.5; fill: none; }
.c1-empty-msg { font-size: 14px; font-weight: 600; color: var(--f1-t2); }
.f1-empty-sub { font-size: 11px; color: var(--f1-t3); }

/* ── Misc ── */
.f1-drawer-icon { width: 36px; height: 36px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.f1-drawer-icon svg { width: 16px; height: 16px; stroke: #fff; stroke-width: 2; fill: none; }
.f1-mono { font-family: "SF Mono","Fira Code",monospace; }

/* ── Password Gate Overlay ── */
.f1-pw-overlay {
  position: fixed; inset: 0; z-index: 500;
  display: flex; align-items: center; justify-content: center;
  background: rgba(244,243,240,0.97);
}
.f1-pw-card {
  background: var(--c1-surface); border: 1px solid var(--c1-border);
  border-radius: 16px; padding: 40px 36px; width: 380px; max-width: 90vw;
  display: flex; flex-direction: column; align-items: center; gap: 14px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.08);
}
.f1-pw-icon { color: var(--c1-accent); }
.f1-pw-icon svg { stroke: var(--c1-accent); }
.f1-pw-title { font-size: 17px; font-weight: 700; color: var(--c1-text); text-align: center; }
.f1-pw-sub { font-size: 12px; color: var(--c1-muted); text-align: center; line-height: 1.5; }
.f1-pw-input {
  width: 100%; padding: 12px 16px; border: 1px solid var(--c1-border);
  border-radius: 8px; font-size: 14px; outline: none;
  background: var(--c1-surface2); color: var(--c1-text);
  text-align: center; letter-spacing: 0.1em;
  transition: border-color 0.15s;
}
.f1-pw-input:focus { border-color: var(--c1-accent); }
.f1-pw-error { font-size: 12px; color: #ef4444; font-weight: 500; }
.f1-pw-submit {
  width: 100%; padding: 12px; border: none; border-radius: 8px;
  background: var(--c1-accent); color: #fff; font-size: 14px;
  font-weight: 600; cursor: pointer; transition: opacity 0.15s;
}
.f1-pw-submit:hover { opacity: 0.9; }
.f1-pw-submit:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── Currency display ── */
.f1-tx-currency-row { display: flex; align-items: center; gap: 6px; }
.f1-tx-currency-badge {
  display: inline-flex; align-items: center; justify-content: center;
  width: 20px; height: 20px; border-radius: 4px;
  background: rgba(99,102,241,0.1); color: var(--c1-accent);
  font-size: 11px; font-weight: 700; flex-shrink: 0;
}
.f1-currency-chip {
  display: inline-flex; padding: 2px 8px; border-radius: 10px;
  background: rgba(99,102,241,0.1); color: var(--c1-accent);
  font-size: 10px; font-weight: 700; letter-spacing: 0.04em;
}

/* ── Finance Tags ── */
.f1-tag-row { display: flex; flex-wrap: wrap; gap: 6px; }
.f1-tag-btn {
  padding: 6px 12px; border-radius: 20px; border: 1px solid var(--c1-border);
  background: var(--c1-surface2); color: var(--f1-t2);
  font-size: 11px; font-weight: 500; cursor: pointer;
  transition: all 0.12s; white-space: nowrap;
}
.f1-tag-btn:hover { border-color: var(--c1-accent); color: var(--c1-text); }
.f1-tag-btn.on {
  background: rgba(99,102,241,0.12); border-color: var(--c1-accent);
  color: var(--c1-accent); font-weight: 600;
}
.f1-tag-reason.on {
  background: rgba(245,158,11,0.12); border-color: #f59e0b;
  color: #d97706;
}

/* ── Responsive ── */
@media (max-width: 400px) {
  .f1-tx-amount { font-size: 13px; }
  .f1-kpi { padding: 0 8px; }
  .f1-kpi-val { font-size: 12px; }
  .f1-td-grid { grid-template-columns: 1fr; }
  .c1-exp-grid { grid-template-columns: 1fr; }
  .f1-mrr-grid { grid-template-columns: 1fr; }
  .c1-prof-panel { width: 100vw; }
}

/* ══ TEAM SCOPE TOGGLE ══════════════════════════════ */
.f1-team-scope-bar {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 12px; background: var(--f1-bg, #F5F4F0);
  border-bottom: 1px solid var(--c1-brd, #e4e3df);
}
.f1-ts-lbl {
  font-size: 11px; font-weight: 600; color: var(--c1-t3, #9ca3af);
  white-space: nowrap;
}
.f1-ts-btns { display: flex; gap: 5px; flex-wrap: wrap; }
.f1-ts-btn {
  font-size: 11px; font-weight: 600; padding: 4px 10px;
  border-radius: 8px; border: 1px solid #ddd; cursor: pointer;
  background: #fff; color: #6b7280; transition: all .12s;
}
.f1-ts-btn:hover { border-color: #a0aec0; }
.f1-ts-btn.on { background: #1e293b; border-color: #1e293b; color: #fff; }
.f1-ts-btn-t1.on { background: #0e7490; border-color: #0e7490; color: #fff; }
.f1-ts-btn-t2.on { background: #7c3aed; border-color: #7c3aed; color: #fff; }

/* ══ TEAM COMPARISON CARDS ══════════════════════════ */
.f1-team-compare-row {
  display: flex; align-items: stretch; gap: 0;
  background: #f8f8f6; border-bottom: 1px solid #e4e3df;
}
.f1-team-compare-single { padding: 0; }
.f1-team-card {
  flex: 1; padding: 12px 16px;
  border-left: 4px solid #ccc;
}
.f1-team-card-t1 { border-left-color: #0e7490; }
.f1-team-card-t2 { border-left-color: #7c3aed; }
.f1-team-card-label { font-size: 11px; color: #6b7280; font-weight: 600; margin-bottom: 4px; }
.f1-team-card-amount { font-size: 18px; font-weight: 700; color: #1e293b; }
.f1-team-vs {
  display: flex; align-items: center; justify-content: center;
  padding: 0 10px; font-size: 11px; color: #9ca3af; font-weight: 700;
  border-left: 1px solid #e4e3df; border-right: 1px solid #e4e3df;
}
.f1-team-nolink {
  flex: 1; padding: 12px 16px; font-size: 12px; color: #9ca3af; font-style: italic;
}
.f1-team-nolink-sm { font-size: 11px; color: #9ca3af; font-style: italic; }

/* ══ TEAM BAR CHART ═════════════════════════════════ */
.f1-team-bar-chart {
  display: flex; gap: 16px; padding: 8px 12px 12px; align-items: flex-end;
}
.f1-team-chart-nodata {
  font-size: 11px; color: #9ca3af; font-style: italic;
  padding: 8px 12px 12px;
}
.f1-tbc-col {
  flex: 1; display: flex; flex-direction: column; align-items: center; gap: 6px;
}
.f1-tbc-track {
  width: 100%; height: 80px; background: #f1f0ec; border-radius: 6px;
  display: flex; align-items: flex-end; overflow: hidden;
}
.f1-tbc-fill {
  width: 100%; border-radius: 6px 6px 0 0; transition: height .4s ease;
  min-height: 4px;
}
.f1-tbc-t1 { background: #0e7490; }
.f1-tbc-t2 { background: #7c3aed; }
.f1-tbc-lbl { font-size: 11px; font-weight: 700; color: #374151; }
.f1-tbc-val { font-size: 12px; color: #6b7280; font-weight: 600; }
</style>
